import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { BaseRCmp } from "abreact/incubator/ui/component/BaseRCmp";
import { CellMeasurerCacheUtils } from "abreact/incubator/ui/datagrid/CellMeasurerCacheUtils";
import { ColumnDropZone } from "abreact/incubator/ui/datagrid/ColumnDropZone";
import { ColumnResizeMgr } from "abreact/incubator/ui/datagrid/ColumnResizeMgr";
import { ColumnVisibilityDlg } from "abreact/incubator/ui/datagrid/ColumnVisibilityDlg";
import { ColumnVisibilityMachine } from "abreact/incubator/ui/datagrid/ColumnVisibilityMachine";
import { DataGridCell } from "abreact/incubator/ui/datagrid/DataGridCell";
import { DataGridCellMeasurementFreshness } from "abreact/incubator/ui/datagrid/DataGridCellMeasurementFreshness";
import { DataGridCellMeasurer } from "abreact/incubator/ui/datagrid/DataGridCellMeasurer";
import { DataGridCellRange } from "abreact/incubator/ui/datagrid/DataGridCellRange";
import { DataGridColumn } from "abreact/incubator/ui/datagrid/DataGridColumn";
import { DataGridKeyboardMgr } from "abreact/incubator/ui/datagrid/DataGridKeyboardMgr";
import { DataGridSelectionMachine, SelectedItem } from "abreact/incubator/ui/datagrid/DataGridSelectionMachine";
import { DataGridSelectionMgr } from "abreact/incubator/ui/datagrid/DataGridSelectionMgr";
import { DataGridUtils } from "abreact/incubator/ui/datagrid/DataGridUtils";
import { DataGridScrollToBottomHelper } from "abreact/incubator/ui/datagrid/DataGridScrollToBottomHelper";
import { MutationObserverForVisibility } from "abreact/incubator/ui/datagrid/MutationObserverForVisibility";
import { LayerGroup } from "abreact/incubator/ui/layer/LayerGroup";
import { MouseMoveTracker } from "abreact/incubator/ui/utils/MouseMoveTracker";
import { mergePropsStyles, ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { PopupManager } from "abreact/popup/PopupManager";
import { AbImage } from "abreact/misc/AbImage";
import { Assert } from "abweb/utils/Assert";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { ElementUtils } from "abweb/utils/ElementUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { ObjectUtils } from "abweb/utils/ObjectUtils";
import { action, observable, when } from "mobx";
import * as React from "react";
import { CellMeasurerCache, Grid, MultiGrid, SortDirection } from "react-virtualized";
import { AbWithTooltip } from "abreact/popup/AbWithTooltip";
export let DataGridScrollSpecTypeEnum = /*#__PURE__*/function (DataGridScrollSpecTypeEnum) {
  DataGridScrollSpecTypeEnum["MAKE_CELL_VISIBLE"] = "MAKE_CELL_VISIBLE";
  DataGridScrollSpecTypeEnum["MAKE_ROW_VISIBLE"] = "MAKE_ROW_VISIBLE";
  DataGridScrollSpecTypeEnum["SCROLL_TO_XY"] = "SCROLL_TO_XY"; // ANCHOR_CELL_TODO = "ANCHOR_CELL",
  return DataGridScrollSpecTypeEnum;
}({});
export let ColumnResizeMode = /*#__PURE__*/function (ColumnResizeMode) {
  ColumnResizeMode[ColumnResizeMode["PRESERVE_WIDTH_PERCENTS"] = 0] = "PRESERVE_WIDTH_PERCENTS";
  ColumnResizeMode[ColumnResizeMode["TRANSITION_TO_FIXED_WIDTHS"] = 1] = "TRANSITION_TO_FIXED_WIDTHS";
  return ColumnResizeMode;
}({});

/**
 * Pattern for managing scroll that's in the style of a "controlled component".
 * This is meant to be an immutable value.
 * properties are not observable, so don't modify properties,
 * instead create new objects using object literal syntax.
 * This use case will help to inform SDK discussions about how to manage scrolling.
 */

export let DataGridCellAlignment = /*#__PURE__*/function (DataGridCellAlignment) {
  DataGridCellAlignment["AUTO"] = "auto";
  DataGridCellAlignment["START"] = "start";
  DataGridCellAlignment["CENTER"] = "center";
  DataGridCellAlignment["END"] = "end";
  return DataGridCellAlignment;
}({});
/**
 * Generic DataGrid component that uses react-virtualized to render HUGE numbers of columns and rows - believe me.
 */
export class DataGrid extends BaseRCmp {
  get cellMeasurementFreshness() {
    if (this._cellMeasurementFreshness === null) {
      this._cellMeasurementFreshness = new DataGridCellMeasurementFreshness();
    }
    this._cellMeasurementFreshness.rowIndexOffset = this.props.showColumnHeaders ? this.fixedRowCount : 0;
    this._cellMeasurementFreshness.columnIndexOffset = this.props.showRowHeaderColumn ? 1 : 0;
    return this._cellMeasurementFreshness;
  }
  get innerGridWidth() {
    let diff = this.props.width - this.state.borderXWidth;
    if (diff > 0) {
      return diff;
    }
    return 0;
  }
  get innerGridHeight() {
    let diff = this.props.height - this.state.borderYWidth;
    if (diff > 0) {
      return diff;
    }
    return 0;
  }
  constructor(props) {
    super(props);
    _defineProperty(this, "cellMeasurerCache", null);
    _defineProperty(this, "visibilityWatcher", null);
    _defineProperty(this, "_selectionMgr", void 0);
    _defineProperty(this, "keyboardMgr", void 0);
    _defineProperty(this, "_currentColumns", void 0);
    // Maintains the ref to the MultiGrid._bottomLeftGrid so we can attach a wheel event handler to it (see MH-31580)
    _defineProperty(this, "bottomLeftGridScrollTarget", null);
    _defineProperty(this, "containerDivRef", void 0);
    // Fields related to column width management
    _defineProperty(this, "_verticalScrollBarSize", 0);
    _defineProperty(this, "inColumnFitMode", false);
    // Used when columnResizeMode = TRANSITION_TO_FIXED_WIDTHS, if true it means that widthPercent is ignored for any column.
    _defineProperty(this, "isFixedWidthMode", false);
    _defineProperty(this, "columnResizeMgr", void 0);
    _defineProperty(this, "_fixedRowCount", 0);
    // _delayedScroll is used to ensure that the grid scrolls to the right row after it loads.
    // for use cases in an onComponentDidMount the bodyGrid ref might be null so we store the
    // position that should have been scrolled to until we get a valid bodyGrid ref
    // then we scroll to it.
    _defineProperty(this, "_delayedScroll", void 0);
    // _delayedScrollPosition is used to ensure that the grid scrolls to the right row after it loads.
    // for use cases in an onComponentDidMount the bodyGrid ref might be null so we store the
    // position that should have been scrolled to until we get a valid bodyGrid ref
    // then we scroll to it.
    _defineProperty(this, "_delayedScrollPosition", void 0);
    _defineProperty(this, "_isScrollingToAnchor", false);
    _defineProperty(this, "_multiGridKey", void 0);
    _defineProperty(this, "_selectionDragUnderway", false);
    _defineProperty(this, "_refreshCount", 0);
    _defineProperty(this, "_columnVisibilityMachine", void 0);
    /** A flag used to keep track of whether or not the anchor cell is  */
    _defineProperty(this, "_isAnchorVisible", false);
    _defineProperty(this, "_keepAnchorVisible", false);
    /** A store to keep track of how fresh cell measurements are (whether they need to be re-measured */
    _defineProperty(this, "_cellMeasurementFreshness", null);
    _defineProperty(this, "defaultGridRowHeightCSSValue", ElementUtils.getCssAsNumber("--ab-default-grid-row-height"));
    this._currentColumns = props.columns;
    this.validateFrozenColumns(props.frozenColumnCount, props.columns);
    const rowHeight = !props.rowHeight && props.useDefaultRowHeight ? this.defaultGridRowHeightCSSValue : props.rowHeight;
    this.state = {
      borderXWidth: 2,
      borderYWidth: 2,
      rowHeight: rowHeight,
      columnDragInfo: null
    };
    this.cellRenderer = this.cellRenderer.bind(this);
    this.cellRendererWithHeaders = this.cellRendererWithHeaders.bind(this);
    this.cellRangeRenderer = this.cellRangeRenderer.bind(this);
    this.groupedHeaderCellRangeRenderer = this.groupedHeaderCellRangeRenderer.bind(this);
    this.cellMeasurerRenderer = this.cellMeasurerRenderer.bind(this);
    this.cellMeasurerRendererWithHeaders = this.cellMeasurerRendererWithHeaders.bind(this);
    this.getColumnWidth = this.getColumnWidth.bind(this);
    this.onSectionRendered = this.onSectionRendered.bind(this);
    this.beforeSectionRendered = this.beforeSectionRendered.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.columnHeaderCellRenderer = this.columnHeaderCellRenderer.bind(this);
    this.setGrid = this.setGrid.bind(this);
    this.setMultiGrid = this.setMultiGrid.bind(this);
    this.onSelectionChangeInternal = this.onSelectionChangeInternal.bind(this);
    this.onFocusCellChangeInternal = this.onFocusCellChangeInternal.bind(this);
    this.onKeyDownHandler = this.onKeyDownHandler.bind(this);
    this.onColumnResizeComplete = this.onColumnResizeComplete.bind(this);
    this.overscanIndicesGetter = this.overscanIndicesGetter.bind(this);
    this.onEditCellFocusHandler = this.onEditCellFocusHandler.bind(this);
    this.onBodyGridCellRangeRendered = this.onBodyGridCellRangeRendered.bind(this);
    this.moveColumns = this.moveColumns.bind(this);
    this.onScrollInFrozenColumns = this.onScrollInFrozenColumns.bind(this);
    this.containerDivRef = /*#__PURE__*/React.createRef();
    let selectionMachine = this.props.selectionMachine || new DataGridSelectionMachine();
    if (props.selectionMode) {
      // Note that selectionMode can either be set on the DG or in the selectionMachine,
      // but if set on the DG, it takes precedence
      selectionMachine.selectionMode = props.selectionMode;
    }
    this._selectionMgr = new DataGridSelectionMgr(this, selectionMachine);
    this._columnVisibilityMachine = this.props.columnVisibilityMachine || new ColumnVisibilityMachine();
    let mx = this._columnVisibilityMachine;
    mx.dataGrid = this;
    this.keyboardMgr = new DataGridKeyboardMgr(this, this.selectionMgr);
    this.initializeCachedColumnInfo(props.columns, props.showColumnHeaders);
    this.initializeSelectionMgr(selectionMachine.selectionMode);
    this.initializeEditingMachine(this.props.editingMachine);
    this.columnResizeMgr = new ColumnResizeMgr(this);
    this.columnResizeMgr.onColumnResizeComplete = this.onColumnResizeComplete;
  }
  /**
   * Initializes cached column info needed for rendering (primarily to support grouped columns).
   * Initializes _fixedRowCount, visibleLeafColumnMap and visibleLeafColumns.
   * @param {DataGridColumn[]} columns Top-level columns
   * @param {boolean} showColumnHeaders
   */
  initializeCachedColumnInfo(columns, showColumnHeaders) {
    this._fixedRowCount = showColumnHeaders ? this.calculateColumnHeaderMaxDepth(columns) : 0;
    this.columnMap = new Map();
    this.visibleLeafColumnMap = new Map();
    this._visibleLeafColumns = [];
    this._leafColumns = DataGridColumn.getLeafColumns(columns, false);
    let columnIndex = 0;
    let rowIndex = 0;
    for (let col of columns) {
      columnIndex += this.initializeCachedColumnInfoHelper(col, rowIndex, columnIndex, this._fixedRowCount, this._visibleLeafColumns);
    }
    for (let n = 0; n < this._visibleLeafColumns.length; n++) {
      this.visibleLeafColumnMap.set(this._visibleLeafColumns[n].id, n);
    }
  }
  initializeCachedColumnInfoHelper(col, rowIndex, columnIndex, fixedRowCount, leafColumns) {
    if (!col.visible) {
      return 0;
    }
    this.columnMap.set(col.id, col);
    let rowSpan = 1;
    if (rowIndex === 0) {
      // Special care needed for top-level column
      // Need to see if it needs to span rows
      let span = fixedRowCount - col.calculateColumnDepth();
      rowSpan += span;
    } else if (col.isLeafColumn && rowIndex + rowSpan < fixedRowCount) {
      // In this case adjust the rowSpan to equal remaining
      rowSpan = fixedRowCount - rowIndex;
    }
    let colSpan = 1;
    if (col.isLeafColumn) {
      // Leaf column
      leafColumns.push(col);
      this.setColumnCellSlot(col, {
        rowIndex,
        columnIndex,
        rowSpan,
        colSpan
      });
      return 1;
    }
    let leafColumnCount = 0;
    for (let childCol of col.childColumns) {
      let childColRowIndex = rowIndex + rowSpan;
      leafColumnCount += this.initializeCachedColumnInfoHelper(childCol, childColRowIndex, columnIndex + leafColumnCount, this._fixedRowCount, leafColumns);
    }
    colSpan = leafColumnCount;
    this.setColumnCellSlot(col, {
      rowIndex,
      columnIndex,
      rowSpan,
      colSpan
    });
    return leafColumnCount;
  }
  initializeEditingMachine(editingMachine) {
    if (editingMachine == null) {
      return;
    }

    // set "friend" prop
    let mx = editingMachine;
    mx.dataGrid = this;
  }
  getLeafColumnsCount() {
    return this._visibleLeafColumns.length;
  }
  initializeSelectionMgr(selectionMode) {
    this.selectionMgr.selectionMachine.selectionMode = selectionMode;
    this.selectionMgr.onSelectionChange = this.onSelectionChangeInternal;
    this.selectionMgr.onFocusCellChange = this.onFocusCellChangeInternal;
  }
  componentDidMount() {
    if (this.cellMeasurerCache) {
      // We are using variable-height rows so add a visibility watcher
      if (this.containerDiv) {
        DataGrid.LOGGER.debug("componentDidMount creating MutationObserverForVisibility");
        this.visibilityWatcher = new MutationObserverForVisibility(this.containerDiv, () => {
          if (this.cellMeasurerCache) {
            this.recomputeGridSize(true);
          }
        });
        this.visibilityWatcher.enable();
      }
    }
    this.postRenderFixups();
    this.recomputeGridBorderSize();
    if (this.props.initialScrolledToCell) {
      this.scrollToCell({
        rowId: this.props.initialScrolledToCell.rowId,
        colId: this.props.initialScrolledToCell.colId
      }, this.props.initialScrolledToCell.position || DataGridCellAlignment.AUTO);
    }
  }
  componentWillUnmount() {
    super.componentWillUnmount();
    this.removeWheelEventListenerOnBottomLeftGrid();
    if (this.visibilityWatcher) {
      this.visibilityWatcher.destroy();
      this.visibilityWatcher = null;
    }
    this.scrollToBottomHelper && this.scrollToBottomHelper.destroy();
  }
  componentWillUpdate(nextProps, nextState, nextContext) {
    // If the anchor was visible after the last update, we will attempt to keep it visible after this update
    this._keepAnchorVisible = this._isAnchorVisible;
    let sizeRecomputed = false;
    // Check for props that may require us to invalidate the cellMeasurerCache
    if (this.shouldInvalidateCellMeasurerCache(nextProps) && this.cellMeasurerCache) {
      DataGrid.LOGGER.debug("DataGrid.componentWillUpdate calling cellMeasurerCache.clearAll()");
      this.cellMeasurementFreshness.clearAll();
      this.recomputeGridSize();
      sizeRecomputed = true;
    }
    let canSelect = this.selectionMgr.canSelect;
    let gridSizeNeedsRecompute = this.gridSizeNeedsRecompute(nextProps);
    if (!canSelect && this.selectionMgr.canSelect || this.props.sortDirection !== nextProps.sortDirection || this.props.sortBy !== nextProps.sortBy || this.props.dataFreshness !== nextProps.dataFreshness || this.props.allowColumnDragToReorder !== nextProps.allowColumnDragToReorder || gridSizeNeedsRecompute) {
      // I do this because we are transitioning from no selection to allowing selection,
      // so I want to make sure we rerender the grid cells so that the onClick handlers are properly attached.
      // if the sortDirection or sortBy changes then the grid needs to rerender
      // since no props passed to react-virtualized change
      if ((this.cellMeasurerCache || this.isVariableRowHeight || gridSizeNeedsRecompute) && !sizeRecomputed) {
        this.recomputeGridSize();
      }
      this.refresh();
    }
    if (!this.props.allowColumnDragToReorder || this.props.onBeforeMoveColumns) {
      // Per MH-27867 if we are in column drag mode, we ignore any changes to props.columns from the outside (unless the onBeforeMoveColumns CB is specified in which case we can change columns from the outside)
      if (this.props.columns !== nextProps.columns || this.props.showColumnHeaders !== nextProps.showColumnHeaders) {
        this._currentColumns = nextProps.columns;
        this.initializeCachedColumnInfo(nextProps.columns, nextProps.showColumnHeaders);
      }
    }
    if (nextProps.frozenColumnCount !== undefined) {
      this.validateFrozenColumns(nextProps.frozenColumnCount, nextProps.columns);
    }
  }
  componentDidUpdate(prevProps, prevState, prevContext) {
    // DataGrid.LOGGER.debug("componentDidUpdate");

    if (!this.bodyGrid) {
      if (this.multiGrid) {
        this.setMultiGrid(this.multiGrid);
      }
      if (this.grid) {
        this.setGrid(this.grid);
      }
    }

    //This is only needed if someone wants to dynamically change row height related props which is very rare
    if (this.props.rowHeight !== prevProps.rowHeight || this.props.useDefaultRowHeight !== prevProps.useDefaultRowHeight) {
      const rowHeight = !this.props.rowHeight && this.props.useDefaultRowHeight ? this.defaultGridRowHeightCSSValue : this.props.rowHeight;
      this.setState({
        rowHeight
      });
    }
    const scrollSpec = this.props.scrollSpec;
    if (prevProps.scrollSpec !== scrollSpec && scrollSpec) {
      if (scrollSpec.kind === DataGridScrollSpecTypeEnum.MAKE_CELL_VISIBLE) {
        this.scrollToCell({
          rowId: scrollSpec.rowId,
          colId: scrollSpec.colId
        });
      }
      if (scrollSpec.kind === DataGridScrollSpecTypeEnum.MAKE_ROW_VISIBLE) {
        this.scrollToCell({
          rowId: scrollSpec.rowId
        });
      }
      if (scrollSpec.kind === DataGridScrollSpecTypeEnum.SCROLL_TO_XY) {
        this.scrollToPosition({
          scrollLeft: scrollSpec.scrollLeft,
          scrollTop: scrollSpec.scrollTop
        });
      }
      // if (scrollSpec.kind === DataGridScrollSpecTypeEnum.ANCHOR_CELL_TODO)
      // {
      //   // TODO - figure out how to thread the anchor value through this
      //   //this.scrollToAnchorViewPortCell(this.isVariableRowHeight);
      // }
    }

    // TODO: Gate behind a prop
    if (scrollSpec == null) {
      if (!this._isScrollingToAnchor) {
        if (this.props.dataFreshness !== prevProps.dataFreshness || this.props.rowCount !== prevProps.rowCount) {
          this._isScrollingToAnchor = true;
          this.scrollToAnchorCell();
        }
      }
    }
    this.postRenderFixups();
    if (prevProps.height !== this.props.height || prevProps.width !== this.props.width) {
      this.recomputeGridBorderSize();
    }
    if (this.props.initialScrolledToCell && (!prevProps.initialScrolledToCell || this.props.initialScrolledToCell.rowId !== prevProps.initialScrolledToCell.rowId || this.props.initialScrolledToCell.colId !== prevProps.initialScrolledToCell.colId)) {
      this.scrollToCell({
        rowId: this.props.initialScrolledToCell.rowId,
        colId: this.props.initialScrolledToCell.colId
      }, this.props.initialScrolledToCell.position || DataGridCellAlignment.AUTO);
    }
  }
  componentWillReceiveProps(nextProps) {
    if (nextProps.selectionMachine && nextProps.selectionMachine !== this.selectionMgr.selectionMachine) {
      this.selectionMgr.selectionMachine = nextProps.selectionMachine;
    }
    if (nextProps.columnVisibilityMachine && nextProps.columnVisibilityMachine !== this._columnVisibilityMachine) {
      this._columnVisibilityMachine = nextProps.columnVisibilityMachine;
      let mx = this._columnVisibilityMachine;
      mx.dataGrid = this;
    }
    this.initializeEditingMachine(nextProps.editingMachine);
    let selectionMode = nextProps.selectionMode != null ? nextProps.selectionMode : this.selectionMgr.selectionMachine.selectionMode;
    this.initializeSelectionMgr(selectionMode);
    this.selectionMgr.selectionMachine.updateSelectionWithNewProps(this.currentColumns, nextProps.columns);
    if (this.props.scrollAnchor !== nextProps.scrollAnchor) {
      this.updateAnchorPosition();
    }
  }
  shouldInvalidateCellMeasurerCache(nextProps) {
    let props = this.props;
    if (nextProps.width !== this.props.width && this.isVariableRowHeight) {
      // if we have a width change, the row heights are likely to change, so we have to invalidate the CellMeasurerCache
      // but only if we are in column fit mode, or if the grid is getting wider and we have at least 1 widthPercent column
      if (this.inColumnFitMode || nextProps.width > this.props.width && this.havePercentWidthColumns()) {
        return true;
      }
    }
    if (nextProps.recomputeGridSizeOnDataFreshnessChange && nextProps.rowCount !== props.rowCount) {
      return true;
    }
    if (nextProps.showColumnHeaders !== props.showColumnHeaders) {
      return true;
    }
    if (nextProps.frozenColumnCount !== props.frozenColumnCount) {
      return true;
    }
    if (nextProps.showRowHeaderColumn !== props.showRowHeaderColumn) {
      return true;
    }
    if (nextProps.columns !== this.currentColumns || nextProps.columns.length !== this.currentColumns.length) {
      return true;
    }
    if (nextProps.recomputeGridSizeOnDataFreshnessChange && nextProps.dataFreshness !== this.props.dataFreshness) {
      return true;
    }
    return false;
  }
  havePercentWidthColumns() {
    for (let col of this.visibleLeafColumns) {
      if (col.widthPercent !== undefined) {
        return true;
      }
    }
    return false;
  }
  get containerDiv() {
    return this.containerDivRef.current;
  }
  render() {
    let {
      showColumnHeaders,
      showRowHeaderColumn
    } = this.props;
    let rowHeight = this.state.rowHeight;
    let width = this.innerGridWidth;
    let height = this.innerGridHeight;
    this.initializeCellMeasurerCache(rowHeight);

    // Both width and height are specified
    let content = showColumnHeaders || showRowHeaderColumn ? this.renderWithHeaders(width, height) : this.renderWithNoHeaders(width, height);
    let commonProps = ReactUtils.componentProps("ab-data-grid", this.props);
    let sizeProps = {
      width: this.innerGridWidth,
      height: this.innerGridHeight
    };
    commonProps.style = mergePropsStyles(commonProps.style, sizeProps);
    return /*#__PURE__*/React.createElement("div", _extends({}, commonProps, {
      onKeyDown: this.onKeyDownHandler,
      "data-test-id": this.props.dataTestId,
      "data-test-component": "ab-data-grid",
      ref: this.containerDivRef
    }), content, this.renderColumnDropZone());
  }
  renderColumnDropZone() {
    if (this.state.columnDragInfo == null) {
      return null;
    }
    let leftOffset = 0;
    if (this.props.showRowHeaderColumn && this.props.rowHeaderWidth !== undefined) {
      leftOffset = this.props.rowHeaderWidth;
    }

    // and add to this leftOffset the width of any frozen columns
    for (let n = 0; n < this.frozenColumnCount; n++) {
      let col = this.currentColumns[n];
      leftOffset += col.actualWidth;
    }
    let containerHeight = this.scrollParams.clientHeight;
    // Need to add the height of the column header
    let divColumnHeaderGrid = this.containerDiv?.querySelector(".ab-is-column-header-grid");
    containerHeight += divColumnHeaderGrid.getBoundingClientRect().height;
    let self = this;
    return /*#__PURE__*/React.createElement(ColumnDropZone, {
      dataGrid: this,
      currentColumns: this.currentColumns,
      columnDragList: this.state.columnDragInfo,
      leftOffset: leftOffset,
      containerHeight: containerHeight,
      onDrop: onDrop
    });
    function onDrop(columnsToMove, insertBeforeColumn) {
      // Note the use of setTimeout() here. I found that it may take nearly a second of execution time to implement the moveColumns() method.
      // I found that in some cases the onDragEnd event would not fire for reasons I don't understand and the columnDragInfo state object never gets cleared.
      setTimeout(() => self.moveColumns(columnsToMove, insertBeforeColumn), 20);
    }
  }
  mapColumnIndexToKey(columnIndex) {
    return columnIndex < 0 ? `${columnIndex}` : this.getColumnIdFromIndex(columnIndex);
  }
  mapRowIndexToKey(rowIndex) {
    return rowIndex < 0 ? `${rowIndex}` : this.getRowIdFromIndex(rowIndex);
  }
  initializeCellMeasurerCache(rowHeight) {
    if (rowHeight === undefined) {
      // We are using variable row height rendering
      if (this.cellMeasurerCache == null) {
        // Initialize the cache
        this.cellMeasurerCache = new CellMeasurerCache({
          defaultWidth: 125,
          fixedWidth: true,
          minHeight: ElementUtils.getCssAsNumber("--ab-default-grid-row-height", 27),
          minWidth: 50,
          keyMapper: (rowIndex, columnIndex) => {
            if (this.props.showRowHeaderColumn) {
              columnIndex = columnIndex - 1;
            }
            if (this.props.showColumnHeaders) {
              rowIndex = rowIndex - this._fixedRowCount;
            }
            return `${this.mapRowIndexToKey(rowIndex)}-${this.mapColumnIndexToKey(columnIndex)}`;
          }
        });
      }
    } else {
      // Using fixed height rows
      // so we don't need this cache
      this.cellMeasurerCache = null;
    }
  }
  renderWithNoHeaders(width, height) {
    let {
      columns,
      rowCount,
      overscrollRowCount,
      overscrollColumnCount,
      showColumnHeaders,
      showRowHeaderColumn
    } = this.props;
    let rowHeight = this.state.rowHeight;
    if (overscrollRowCount !== undefined && overscrollRowCount > 0) {
      rowCount = rowCount + overscrollRowCount;
    }
    let columnCount = columns.length;
    if (overscrollColumnCount !== undefined && overscrollColumnCount > 0) {
      columnCount = columnCount + overscrollColumnCount;
    }
    if (width === undefined) {
      // AutoSizer case where width is not managed
      width = this.innerGridWidth;
    }
    if (height === undefined) {
      // AutoSizer case where height is not managed
      height = this.innerGridHeight;
    }
    if (showColumnHeaders || showRowHeaderColumn) {
      return null;
    }
    this._fixedRowCount = 0;

    // Compute column widths
    this.updateColumnWidths(width);
    let estimatedColumnSize = this.estimateColumnSize();
    if (rowHeight === undefined) {
      // Need to use CellMeasurer
      // Note that in an attempt to improve horizontal scrolling performance, we use a slightly higher value of overscanColumnCount
      // since we have to render (measure) all the columns anyway in order to determine the row height
      return /*#__PURE__*/React.createElement(Grid, {
        cellRenderer: this.cellMeasurerRenderer,
        className: "ab-is-content-grid",
        overscanColumnCount: 5,
        overscanRowCount: 10,
        overscanIndicesGetter: this.overscanIndicesGetter,
        columnCount: columnCount,
        columnWidth: this.getColumnWidth,
        estimatedColumnSize: estimatedColumnSize,
        deferredMeasurementCache: this.cellMeasurerCache,
        onSectionRendered: this.onSectionRendered,
        onScroll: this.onScroll,
        scrollToAlignment: DataGridCellAlignment.AUTO,
        height: height,
        rowCount: rowCount,
        rowHeight: this.cellMeasurerCache.rowHeight,
        width: width,
        ref: this.setGrid,
        tabIndex: this.props.tabIndex
      });
    } else {
      return /*#__PURE__*/React.createElement(Grid, {
        cellRenderer: this.cellRenderer,
        className: "ab-is-content-grid",
        overscanColumnCount: 1,
        overscanRowCount: 10,
        overscanIndicesGetter: this.overscanIndicesGetter,
        columnCount: columnCount,
        columnWidth: this.getColumnWidth,
        estimatedColumnSize: estimatedColumnSize,
        height: height,
        rowCount: rowCount,
        rowHeight: rowHeight,
        width: width,
        onSectionRendered: this.onSectionRendered,
        onScroll: this.onScroll,
        scrollToAlignment: DataGridCellAlignment.AUTO,
        ref: this.setGrid,
        tabIndex: this.props.tabIndex
      });
    }
  }

  /**
   * Render a MultiGrid which includes a row header and a column header
   * @param {number} width
   * @param {number} height
   * @returns {React.ReactNode}
   */
  renderWithHeaders(width, height) {
    let {
      rowCount,
      overscrollRowCount,
      overscrollColumnCount,
      showColumnHeaders,
      showRowHeaderColumn
    } = this.props;
    let rowHeight = this.state.rowHeight;
    const frozenColumnCount = this.frozenColumnCount;
    if (overscrollRowCount !== undefined && overscrollRowCount > 0) {
      rowCount = rowCount + overscrollRowCount;
    }
    if (width === undefined) {
      // AutoSizer case where width is not managed
      width = this.innerGridWidth;
    }
    if (height === undefined) {
      // AutoSizer case where height is not managed
      height = this.innerGridHeight;
    }
    let fixedRowCount = this._fixedRowCount;
    let fixedColumnCount = showRowHeaderColumn ? 1 : 0;
    if (frozenColumnCount > 0) {
      fixedColumnCount += frozenColumnCount;
    }
    let columnCount = this._visibleLeafColumns.length + fixedColumnCount - frozenColumnCount;
    if (overscrollColumnCount !== undefined && overscrollColumnCount > 0) {
      columnCount = columnCount + overscrollColumnCount;
    }

    // <stringsAreClassNames>
    let gridHeadClasses = (showColumnHeaders ? " ab-has-col-headers" : "") + (showRowHeaderColumn ? " ab-has-row-headers" : "");
    let topLeftGridClass = "ab-is-top-left-grid ab-is-header-grid" + gridHeadClasses;
    let topRightGridClass = "ab-is-column-header-grid ab-is-header-grid" + gridHeadClasses;
    let bottomLeftGridClass = "ab-is-row-header-grid ab-is-header-grid" + gridHeadClasses;
    let bottomRightGridClass = "ab-is-content-grid" + gridHeadClasses;
    if (frozenColumnCount > 0) {
      topLeftGridClass += " ab-has-frozen-columns";
      bottomLeftGridClass += " ab-has-frozen-columns";
    }
    // </stringsAreClassNames>

    // Compute column widths
    if (width > 0) {
      this.updateColumnWidths(width);
    }
    let estimatedColumnSize = this.estimateColumnSize();
    if (rowHeight === undefined) {
      // Need to use CellMeasurer

      // TODO: Not sure what to do about col header rowHeight here

      // Note that we use a key on each because I found that if you dynamically switch between fixed height
      // and variable-height rows, the cellMeasurerCache was stale. Using a key for this case
      // forces a new MultiGridInstance which eliminates this issue.
      // _fixedRowCount and fixedColumnCount are added here to work around a react-virtualized
      // issue where these values are only accounted for in the constructor
      let key = "MultiGrid_CellMeasurer_" + fixedRowCount + "_" + fixedColumnCount;
      let scrollProps = {};
      if (key !== this._multiGridKey) {
        this._multiGridKey = key;
        if (this._scrollParams) {
          scrollProps.scrollTop = this._scrollParams.scrollTop;
          scrollProps.scrollLeft = this._scrollParams.scrollLeft;
        }
      }

      // Note that in an attempt to improve horizontal scrolling performance, we use a slightly higher value of overscanColumnCount
      // since we have to render (measure) all the columns anyway in order to determine the row height
      return /*#__PURE__*/React.createElement(MultiGrid, _extends({
        key: key,
        fixedColumnCount: fixedColumnCount,
        fixedRowCount: fixedRowCount,
        enableFixedColumnScroll: false,
        enableFixedRowScroll: false,
        columnCount: columnCount,
        columnWidth: this.getColumnWidth,
        estimatedColumnSize: estimatedColumnSize,
        overscanColumnCount: 5,
        overscanRowCount: 10,
        overscanIndicesGetter: this.overscanIndicesGetter,
        width: width,
        height: height,
        rowHeight: this.cellMeasurerCache.rowHeight,
        rowCount: rowCount + fixedRowCount,
        cellRenderer: this.cellMeasurerRendererWithHeaders,
        cellRangeRenderer: this.cellRangeRenderer,
        scrollToAlignment: DataGridCellAlignment.AUTO,
        onSectionRendered: this.onSectionRendered,
        onScroll: this.onScroll,
        deferredMeasurementCache: this.cellMeasurerCache,
        classNameTopLeftGrid: topLeftGridClass,
        classNameBottomLeftGrid: bottomLeftGridClass,
        classNameTopRightGrid: topRightGridClass,
        classNameBottomRightGrid: bottomRightGridClass,
        ref: this.setMultiGrid,
        tabIndex: this.props.tabIndex
      }, scrollProps));
    } else {
      // Using fixed-height rows or a rowHeight function
      return /*#__PURE__*/React.createElement(MultiGrid, {
        key: "MultiGrid_rowHeight",
        fixedColumnCount: fixedColumnCount,
        fixedRowCount: fixedRowCount,
        enableFixedColumnScroll: false,
        enableFixedRowScroll: false,
        columnCount: columnCount,
        columnWidth: this.getColumnWidth,
        estimatedColumnSize: estimatedColumnSize,
        overscanColumnCount: 1,
        overscanRowCount: 10,
        overscanIndicesGetter: this.overscanIndicesGetter,
        width: width,
        height: height,
        rowHeight: rowHeight,
        rowCount: rowCount + fixedRowCount,
        cellRenderer: this.cellRendererWithHeaders,
        cellRangeRenderer: this.cellRangeRenderer,
        scrollToAlignment: DataGridCellAlignment.AUTO,
        onSectionRendered: this.onSectionRendered,
        classNameTopLeftGrid: topLeftGridClass,
        onScroll: this.onScroll,
        classNameBottomLeftGrid: bottomLeftGridClass,
        classNameTopRightGrid: topRightGridClass,
        classNameBottomRightGrid: bottomRightGridClass,
        ref: this.setMultiGrid,
        tabIndex: this.props.tabIndex
      });
    }
  }
  setGrid(ref) {
    this.multiGrid = null;
    this.grid = ref;
    if (this._delayedScroll) {
      let [{
        rowId,
        colId
      }, position] = this._delayedScroll;
      this._delayedScroll = undefined;
      this.safelyScrollToCell({
        rowId,
        colId
      }, position);
    }
    if (this._delayedScrollPosition) {
      let {
        scrollLeft,
        scrollTop
      } = this._delayedScrollPosition;
      this._delayedScrollPosition = undefined;
      this.safelyScrollToPosition({
        scrollLeft,
        scrollTop
      });
    }
  }
  setMultiGrid(ref) {
    this.grid = null;
    this.multiGrid = ref;
    if (ref == null) {
      this.removeWheelEventListenerOnBottomLeftGrid();
    }
    if (this.frozenColumnCount > 0 && this.multiGrid) {
      // Per MH-31580, the thumbwheel scroll does not work in the frozen columns section due to some react-virtualized issues (see ticket for details)
      // so we will handle the wheel event and scroll using our APIs
      this.addWheelEventListenerOnBottomLeftGrid();

      // We have frozen columns and we'd like to be able to give focus to the cells in those columns,
      // but react-virtualized MultiGrid does not set a tabindex on the bottom left grid so we need to hack the DOM
      const mg = this.multiGrid;
      if (mg._bottomLeftGrid?._scrollingContainer) {
        let tabIndex = this.props.tabIndex === undefined ? 0 : this.props.tabIndex;
        if (tabIndex >= 0) {
          mg._bottomLeftGrid._scrollingContainer.setAttribute("tabindex", tabIndex);
        }
      }
    }
    if (!this.scrollToBottomHelper && this.bodyGrid && this.isVariableRowHeight) {
      /*
      For variable-rowheight grids, scrolling to the bottom of the grid can be a problem due to row measurement
      See DataGridScrollToBottomHelper for more info
       */
      this.scrollToBottomHelper = new DataGridScrollToBottomHelper(this.bodyGrid, () => this.forceScrollToBottom());
    }
    if (this._delayedScroll) {
      let [{
        rowId,
        colId
      }, position] = this._delayedScroll;
      this._delayedScroll = undefined;
      this.safelyScrollToCell({
        rowId,
        colId
      }, position);
    }
    if (this._delayedScrollPosition) {
      let {
        scrollLeft,
        scrollTop
      } = this._delayedScrollPosition;
      this._delayedScrollPosition = undefined;
      this.safelyScrollToPosition({
        scrollLeft,
        scrollTop
      });
    }
  }

  /**
   * Adds a wheel event handler to the MultiGrid._bottomLeftGrid which will be used to scroll.
   * @private
   */
  addWheelEventListenerOnBottomLeftGrid() {
    this.removeWheelEventListenerOnBottomLeftGrid();
    if (this.multiGrid) {
      const mg = this.multiGrid;
      let gridDiv = mg._bottomLeftGrid?._scrollingContainer;
      if (gridDiv) {
        // Per MH-31580, set up our wheel event listener in the bottomLeftGrid

        // eslint-disable-next-line abinitio/check-listeners
        gridDiv.addEventListener("wheel", this.onScrollInFrozenColumns, {
          passive: true
        });
        this.bottomLeftGridScrollTarget = gridDiv;
      }
    }
  }
  removeWheelEventListenerOnBottomLeftGrid() {
    if (this.bottomLeftGridScrollTarget == null) {
      return;
    }
    this.bottomLeftGridScrollTarget.removeEventListener("wheel", this.onScrollInFrozenColumns);
    this.bottomLeftGridScrollTarget = null;
  }
  onScrollInFrozenColumns(event) {
    // Compute the new scroll position subject to the lower and upper limits
    let scrollTop = Math.max(this.scrollParams.scrollTop + event.deltaY, 0);
    scrollTop = Math.min(scrollTop, this.scrollParams.scrollHeight - this.scrollParams.clientHeight);
    // console.log(`GOT wheel EVENT!!! deltaY: ${event.deltaY} scrollParams.scrollTop: ${this.scrollParams.scrollTop} scrollTop: ${scrollTop}`);

    this.scrollToPosition({
      scrollTop
    });
  }

  /**
   * Returns the body grid.
   * @private
   */
  get bodyGrid() {
    // Per MH-22782, we found that setting display: none on any ancestor component of the DataGrid makes react-virtualized unmount the _bottomRightGrid,
    // so instead of caching bodyGrid, we make it a getter.
    if (this.multiGrid) {
      return this.multiGrid._bottomRightGrid;
    } else if (this.grid) {
      return this.grid;
    } else {
      return null;
    }
  }

  /**
   * Helper method used to get the content to render inside the body of the grid
   * in the div that represents a cell.
   * This method may either use the user-supplied props.cellRenderer, a column-specific cellRenderer,
   * or the props.getCellValue callback to get a canonical value.
   * Note that all cellRenderer paths end up here.
   * @param {} props
   * @param getCellRef
   * @returns {any}
   */
  getCellContent(props, getCellRef) {
    let dgCol = this._visibleLeafColumns[props.columnIndex];
    let content = null;
    if (this.isEditCell(props.rowIndex, props.columnIndex)) {
      // This is the cell being edited
      let editingMachine = this.props.editingMachine;
      if (dgCol.editCellRenderer) {
        content = dgCol.editCellRenderer(props, editingMachine);
      } else if (editingMachine.editCellRenderer) {
        content = editingMachine.editCellRenderer(props, editingMachine);
      }
      if (getCellRef && dgCol.commitOnFocusLoss) {
        this.setUpEditCellFocusListener(getCellRef);
      }
    }
    if (content == null) {
      if (dgCol.cellRenderer) {
        content = dgCol.cellRenderer(props);
      } else if (this.props.cellRenderer) {
        content = this.props.cellRenderer(props);
      } else {
        if (this.props.getCellValue) {
          let rawValue = this.props.getCellValue(props.rowIndex, props.columnIndex);
          content = DataGridUtils.canonicalTypeToString(rawValue);
        } else {
          DataGrid.LOGGER.info("cellRenderer: You need to specify either a cellRenderer or a getCellValue callback");
          content = "";
        }
      }
    }
    return content;
  }
  async setUpEditCellFocusListener(getCellRef) {
    const cellRef = await getCellRef();
    this._editCellDiv = cellRef.divRef;
    if (this._editCellDiv && this._editCellDiv.ownerDocument) {
      // react 17 doesn't send events to document.  Using the workaround in https://reactjs.org/blog/2020/08/10/react-v17-rc.html#fixing-potential-issues
      this._editCellDiv.ownerDocument.addEventListener("focusin", this.onEditCellFocusHandler, {
        capture: true
      });
    }
  }

  /**
   * Internal cellRenderer used to delegate to user-supplied Grid.cellRenderer or DataGridColumn.cellRenderer
   * or to provide a simple generic renderer if getCellValue is specified.
   * @param {} props
   * @returns {any}
   */
  cellRenderer(props) {
    if (props.rowIndex >= this.props.rowCount || props.columnIndex >= this._visibleLeafColumns.length) {
      return /*#__PURE__*/React.createElement(OverscrollCell, props);
    }
    props.useNativeTooltip = this.props.useNativeTooltipOnCells;
    props.cellValueGetter = this.props.getCellValue;
    let dgColumn = this._visibleLeafColumns[props.columnIndex];

    // <stringsAreClassNames> -- see checkCss.ksh
    let classNames = "ab-content-grid-cell";
    if (props.columnIndex === this.frozenColumnCount - 1) {
      // We are rendering locked columns and this is the last locked column
      classNames += " ab-last-frozen-column-cell";
    }
    if (dgColumn.applyDefaultStyles || dgColumn.cellRenderer == null && this.props.cellRenderer == null) {
      // Using default rendering so inject class
      if (this.state.rowHeight) {
        // Fixed row height
        classNames += " grid-no-wrap-cell";
      } else {
        classNames += " grid-wrap-cell";
      }
    }
    let isEditCell = this.isEditCell(props.rowIndex, props.columnIndex);
    if (isEditCell) {
      classNames += " ab-edit-cell";
    }
    if (this.selectionMgr.canSelect) {
      classNames += " ab-select-mode";
    }
    if (props.rowIndex % 2 !== 0) {
      let altRowClassName = this.getAltRowClassName();
      if (altRowClassName != null) {
        classNames += " " + altRowClassName;
      }
    }
    if (props.classNames) {
      classNames += " " + props.classNames;
    }
    let colId = this.getColumnIdFromIndex(props.columnIndex);
    let rowId = this.getRowIdFromIndex(props.rowIndex);
    if (this.selectionMgr.isSelected(rowId, colId)) {
      classNames += " ab-selected";
    }
    if (this.selectionMgr.isFocusCell(rowId, colId)) {
      classNames += " ab-focused";
    }
    // </stringsAreClassNames>

    // THIS IS A TEST
    if (props.rowIndex === 4 && props.columnIndex === 0) {
      // DataGrid.LOGGER.debug("cellRenderer: Rendering (4, 0) now.");
    }
    let colWidth = props.style.width;
    if (this.cellMeasurerCache) {
      // We are using a <CellMeasurer> which requires explicit width to be set
      // but note that use of getColumnWidth() requires absolute colIndex so we have to add columnOffset back in
      let columnOffset = this.props.showRowHeaderColumn ? 1 : 0;
      colWidth = this.getColumnWidth({
        index: props.columnIndex + columnOffset
      });
    }
    if (dgColumn.classNames) {
      classNames += " " + dgColumn.classNames;
    }
    if (dgColumn.getClassNames) {
      classNames += " " + dgColumn.getClassNames(props);
    }
    if (dgColumn.getWidthOverride) {
      const widthOverride = dgColumn.getWidthOverride(props);
      if (widthOverride !== undefined) {
        colWidth = widthOverride;
      }
    }
    let mouseDownHandler;
    let mouseEnterHandler;
    if (this.selectionMgr.canSelect) {
      mouseDownHandler = this.onBodyCellMouseDown.bind(this, props.rowIndex, props.columnIndex);
      mouseEnterHandler = this.onBodyCellMouseEnter.bind(this, props.rowIndex, props.columnIndex);
    }
    let doubleClickHandler;
    if (this.editable && dgColumn.editable && this.props.editingMachine.doubleClickToEditCell && this.selectionMgr.canSelectCells && !isEditCell) {
      // Attach double click handler to engage cell editing
      doubleClickHandler = this.onBodyCellDoubleClick.bind(this, props.rowIndex, props.columnIndex);
    }
    const forceCellUpdate = this.props.shouldCellUpdate && this.props.shouldCellUpdate({
      colId,
      rowId
    });

    // Note that props.rowIndex, props.columnIndex have been adjusted for the body grid with (0, 0) being the first cell,
    // but if we have row and/or column headers we need to adjust them again

    let cellRef = observable.box();

    // Prefer to use semantic data to build cell key to avoid React state problems when re-ordering rows and columns.
    const cellKey = this.props.rowIndexToIdMapper ? `${this.props.rowIndexToIdMapper(props.rowIndex)}_${dgColumn.id}` : props.key;
    return /*#__PURE__*/React.createElement(DataGridCell, {
      key: cellKey,
      classNames: classNames,
      onMouseDown: mouseDownHandler,
      onMouseEnter: mouseEnterHandler,
      onDoubleClick: doubleClickHandler,
      column: dgColumn,
      columnWidth: colWidth,
      cellMeasurerCache: this.cellMeasurerCache,
      dataFreshness: this.props.dataFreshness,
      refreshCount: this._refreshCount,
      forceUpdate: forceCellUpdate,
      getCellContent: (cellProps, getCellRef) => this.getCellContent(cellProps, getCellRef),
      getCellRef: async () => {
        await when(() => cellRef.get() !== undefined);
        return cellRef.get();
      },
      gridCellProps: {
        ...props
      },
      cellMeasurementFreshness: this.cellMeasurementFreshness,
      ref: action(ref => {
        if (ref) {
          cellRef.set(ref);
        }
      })
    });
  }

  /**
   * Gets the rowIndex, columnIndex tuple in the form needed for calls into the DataGridCellMeasurementFreshness object.
   * @param rowId
   * @param colId
   * @private
   */
  getCellMeasurerCachePosition(rowId, colId) {
    let rowIndexOffset = this.props.showColumnHeaders ? this.fixedRowCount : 0;
    let rowIndex = this.props.rowIdToIndexMapper ? this.props.rowIdToIndexMapper(rowId) : rowId;
    rowIndex += rowIndexOffset;
    let colIndexOffset = this.props.showRowHeaderColumn ? 1 : 0;
    let visibleColIndex = this.getColumnIndexFromId(colId);
    if (visibleColIndex < 0) {
      return null;
    }
    return {
      rowIndex,
      columnIndex: colIndexOffset + visibleColIndex
    };
  }
  get editable() {
    return this.props.editingMachine ? this.props.editingMachine.editable : false;
  }

  /**
   * Cell renderer used when using a MultiGrid to render data with row and column headers.
   * @param {} props
   * @returns {any}
   */
  cellRendererWithHeaders(props) {
    let rowOffset = this._fixedRowCount;
    let columnOffset = this.props.showRowHeaderColumn ? 1 : 0;
    let rowIndex = props.rowIndex - rowOffset;
    let columnIndex = props.columnIndex - columnOffset;
    if (rowIndex < 0) {
      if (columnIndex < 0) {
        // top-left cell
        return this.topLeftHeaderCellRenderer(props);
      } else {
        // column header
        if (this.props.rowHeightFunctionCellMeasurerFactory) {
          const factoryProps = {
            rowIndex,
            columnIndex,
            style: props.style
          };
          return this.props.rowHeightFunctionCellMeasurerFactory(factoryProps, this.columnHeaderCellRenderer(props));
        } else {
          return this.columnHeaderCellRenderer(props);
        }
      }
    } else {
      if (columnIndex < 0) {
        // row header
        return this.rowHeaderCellRenderer(props, rowOffset);
      } else {
        if (this.props.rowHeightFunctionCellMeasurerFactory) {
          const factoryProps = {
            rowIndex,
            columnIndex,
            style: props.style
          };
          return this.props.rowHeightFunctionCellMeasurerFactory(factoryProps, this.gridBodyCellRenderer(props, rowOffset, columnOffset));
        } else {
          return this.gridBodyCellRenderer(props, rowOffset, columnOffset);
        }
      }
    }
  }
  gridBodyCellRenderer(props, rowOffset, columnOffset) {
    // adjust props for grid without headers
    props.rowIndex -= rowOffset;
    props.columnIndex -= columnOffset;
    let content = this.cellRenderer(props);
    props.rowIndex += rowOffset;
    props.columnIndex += columnOffset;
    return content;
  }

  /**
   * Internal cellRenderer used to delegate to user-supplied Grid.cellRenderer or DataGridColumn.cellRenderer
   * or to provide a simple generic renderer if getCellValue is specified.
   * @param {} props
   * @returns {any}
   */
  cellMeasurerRenderer(props) {
    const cellWrapper = this.cellRenderer(props);
    return this.renderCellMeasurer(props, cellWrapper);
  }
  cellMeasurerRendererWithHeaders(props) {
    let cellWrapper;
    const rowOffset = this._fixedRowCount;
    const columnOffset = this.props.showRowHeaderColumn ? 1 : 0;
    const rowIndex = props.rowIndex - rowOffset;
    const columnIndex = props.columnIndex - columnOffset;
    if (rowIndex < 0) {
      if (columnIndex < 0) {
        // top-left cell
        cellWrapper = this.topLeftHeaderCellRenderer(props);
      } else {
        // column header
        // delegate to columnHeaderCellRenderer
        cellWrapper = this.columnHeaderCellRenderer(props);
      }
    } else {
      if (columnIndex < 0) {
        // row header
        cellWrapper = this.rowHeaderCellRenderer(props, rowOffset);
      } else {
        // body cell
        cellWrapper = this.gridBodyCellRenderer(props, rowOffset, columnOffset);
      }
    }
    if (cellWrapper == null) {
      // We can get null here when rendering grouped columns and
      // apparently the CellMeasurer does not like having null children
      cellWrapper = "";
    }
    return this.renderCellMeasurer(props, cellWrapper);
  }
  renderCellMeasurer(props, cellWrapper) {
    return /*#__PURE__*/React.createElement(DataGridCellMeasurer, {
      cache: this.cellMeasurerCache,
      columnIndex: props.columnIndex,
      key: props.key,
      parent: props.parent,
      rowIndex: props.rowIndex,
      style: props.style,
      cellMeasurementFreshness: this.cellMeasurementFreshness
    }, cellWrapper);
  }
  getColumnWidth(params) {
    let columnOffset = this.props.showRowHeaderColumn ? 1 : 0;
    let columnIndex = params.index - columnOffset;
    if (columnIndex < 0) {
      // row header column
      return this.props.rowHeaderWidth;
    } else if (columnIndex >= this._visibleLeafColumns.length) {
      // This is the column width used for overscroll columns (the number of pixels of white space per column)
      return 50;
    } else {
      return this.getColumnCurrentWidth(this._visibleLeafColumns[columnIndex]);
    }
  }
  estimateColumnSize() {
    let total = 0;
    let columnCount = this._visibleLeafColumns.length;
    for (let n = 0; n < columnCount; n++) {
      total += this.getColumnCurrentWidth(this._visibleLeafColumns[n]);
    }
    if (this.props.showRowHeaderColumn) {
      total += this.props.rowHeaderWidth;
      columnCount++;
    }
    return total / columnCount;
  }

  /**
   * Helper method to set the "internal" DataGridColumn.currentWidth value.
   * @param {DataGridColumn} col
   * @param {number} width
   * @return true if the width has actually changed, false otherwise
   */
  setColumnCurrentWidth(col, width) {
    const c = col;
    const changed = c.currentWidth !== width;
    c.currentWidth = width;
    return changed;
  }
  getColumnCurrentWidth(col) {
    const c = col;
    return c.currentWidth;
  }

  /**
   * Helper method to set the "internal" DataGridColumn.userWidth value.
   * @param {DataGridColumn} col
   * @param {number} width
   */
  setColumnUserWidth(col, width) {
    const c = col;
    c.userWidth = width;
  }
  getColumnUserWidth(col) {
    const c = col;
    return c.userWidth;
  }
  setColumnCellSlot(col, cellSlot) {
    const c = col;
    c._cellSlot = cellSlot;
  }

  /**
   * Called from render() to compute the column widths to use
   * @param {number} bodyGridWidth Total width of DataGrid. This will include rowHeaderWidth.
   * @returns {boolean} true if new colWidths differ from previous ones.
   */
  updateColumnWidths(bodyGridWidth) {
    let colWidths = [];
    this.inColumnFitMode = false;
    bodyGridWidth -= this._verticalScrollBarSize;
    if (this.props.showRowHeaderColumn) {
      bodyGridWidth -= this.props.rowHeaderWidth;
    }
    let columns = this._visibleLeafColumns;
    if (this.frozenColumnCount > 0) {
      // We have frozen columns
      // so we have to reduce bodyGridWidth by their widths
      for (let n = 0; n < this.frozenColumnCount; n++) {
        // Note that we can't use col.actualWidth here because the first time through, this may not not be set
        let col = columns[n];
        let userWidth = this.getColumnUserWidth(col);
        if (userWidth < 0) {
          userWidth = col.getColumnWidth(n);
        }
        bodyGridWidth -= userWidth;
      }

      // and they should not be included in these calculations
      columns = columns.slice(this.frozenColumnCount);
    }
    let pctWidthCols = [];
    let totalFixedWidth = 0;
    let pctSum = 0;

    // First walk is to determine fixed width cols and percent width cols
    for (let index = 0; index < columns.length; index++) {
      let column = columns[index];
      if (this.getColumnUserWidth(column) > 0) {
        // User has changed column size so this takes precedence
        let w = colWidths[index] = this.getColumnUserWidth(column);
        totalFixedWidth += w;
      } else if (column.widthPercent) {
        // This is a pct width column
        if (this.isFixedWidthMode) {
          // This means that columnWidthMode = TRANSITION_TO_FIXED_WIDTHS and user has changed width of column
          // so we transition to ALL columns having a fixed width
          this.inColumnFitMode = false;
          let w = colWidths[index] = this.getColumnCurrentWidth(column);
          totalFixedWidth += w;
        } else {
          pctWidthCols.push({
            index,
            column,
            pct: column.widthPercent
          });
          pctSum += column.widthPercent;
          this.inColumnFitMode = true;
        }
      } else {
        let w = colWidths[index] = column.getColumnWidth(index);
        totalFixedWidth += w;
      }
    }
    if (pctWidthCols.length > 0) {
      // Have to work on pct width cols now
      // remainder is the amount to divvy up
      let remainder = bodyGridWidth - totalFixedWidth;
      if (remainder <= 0) {
        // This means our totalFixedWidth is wider than VP
        // so interpret the pct values as pct of VP
        remainder = bodyGridWidth;
        this.inColumnFitMode = false;
      }
      for (let n = 0; n < pctWidthCols.length; n++) {
        let item = pctWidthCols[n];
        let column = columns[item.index];
        if (pctSum !== 100) {
          // Normalize to 100%
          item.pct *= 100 / pctSum;
        }
        let w = remainder * item.pct / 100;
        if (w < column.minWidth) {
          // Can't use pct here because of minWidth constraint
          // so this becomes a fixed width column
          w = column.minWidth;
          this.inColumnFitMode = false;
        }
        colWidths[item.index] = w;
        totalFixedWidth += w;
      }
    }
    let changedColumns = [];
    for (let index = 0; index < this.frozenColumnCount; index++) {
      let col = this._visibleLeafColumns[index];
      let userWidth = this.getColumnUserWidth(col);
      if (userWidth < 0) {
        userWidth = col.getColumnWidth(index);
      }
      if (this.setColumnCurrentWidth(col, userWidth)) {
        changedColumns.push(col);
      }
    }

    // update real values
    for (let n = 0; n < columns.length; n++) {
      if (this.setColumnCurrentWidth(columns[n], colWidths[n])) {
        changedColumns.push(columns[n]);
      }
    }
    this.updateGroupedColumnWidths(this.currentColumns, changedColumns);

    // TODO: Might have to check for rounding error
    DataGrid.LOGGER.debug(`updateColumnWidths bodyGridWidth: ${bodyGridWidth} totalFixedWidth: ${totalFixedWidth} inColumnFitMode: ${this.inColumnFitMode} changedColumns.length: ${changedColumns.length}`);
    if (changedColumns.length > 0) {
      this.props.onColumnWidthsChange?.(changedColumns);
    }
  }

  /**
   * Called from updateColumnWidths() after we've updated the leaf column widths.
   * This method computes the currentWidth for all grouped columns based on leaf column widths.
   * @param {DataGridColumn[]} columns top-level columns
   */
  updateGroupedColumnWidths(columns, changedColumns) {
    if (!this.haveGroupedColumns) {
      // We don't have grouped columns, so nothing to do
      return;
    }

    // TODO: Optimize by summing widths of children first
    for (let col of columns) {
      if (!col.visible) {
        continue;
      }
      if (!col.isLeafColumn) {
        let leafColumns = col.getAllLeafColumns(true);
        let totalWidth = 0;
        for (let leafCol of leafColumns) {
          totalWidth += this.getColumnCurrentWidth(leafCol);
        }
        if (this.setColumnCurrentWidth(col, totalWidth)) {
          changedColumns.push(col);
        }

        // Drill into children in case we have multiple groups
        this.updateGroupedColumnWidths(col.childColumns, changedColumns);
      }
    }
  }
  calculateColumnHeaderMaxDepth(columns) {
    let depth = 0;
    for (let col of columns) {
      depth = Math.max(depth, col.calculateColumnDepth());
    }
    return depth;
  }
  get haveGroupedColumns() {
    return this._fixedRowCount > 1;
  }

  /**
   * Cell renderer for Grid that renders top-left empty cell.
   * @param {} props
   * @returns {React.ReactNode}
   */
  topLeftHeaderCellRenderer(props) {
    let cellContent = null;
    if (this.props.topLeftHeaderCellRenderer) {
      cellContent = this.props.topLeftHeaderCellRenderer(props);
    }
    let classNames = "ab-top-left-cell";
    if (this.selectionMgr.canSelect) {
      classNames += " ab-select-mode";
    }
    let styles = props.style;
    let dgCellProps = props;
    if (dgCellProps.classNames) {
      classNames += " " + dgCellProps.classNames;
    }
    if (dgCellProps.styles || this.fixedRowCount > 1) {
      styles = {
        ...props.style,
        ...dgCellProps.styles
      };
    }
    if (this.fixedRowCount > 1 && typeof styles.height === "number") {
      // We are rendering the header cell of the topLeftGrid AND we have grouped columns
      // In this case, the styles.height isn't computed correctly so we will tweak it here
      const mgr = props.parent._rowSizeAndPositionManager;
      if (mgr) {
        styles.height = Math.max(styles.height, mgr.getTotalSize());
      }
    }
    let clickHandler;
    if (this.selectionMgr.isMultipleSelectionMode) {
      clickHandler = this.onTopLeftCellClick.bind(this);
    }
    return /*#__PURE__*/React.createElement("div", {
      key: props.key,
      onClick: clickHandler,
      className: classNames,
      style: styles
    }, cellContent);
  }

  /**
   * Cell renderer for row header Grid.
   * @param {} props
   * @param {number} rowOffset
   * @returns {any}
   */
  rowHeaderCellRenderer(props, rowOffset) {
    let rowIndex = props.rowIndex - rowOffset;
    if (rowIndex >= this.props.rowCount) {
      return /*#__PURE__*/React.createElement(OverscrollCell, props);
    }

    // <stringsAreClassNames>
    let classNames = "ab-row-header-cell";
    if (this.selectionMgr.canSelect) {
      classNames += " ab-select-mode";
    }
    if (this.props.rowHeaderCellRenderer == null) {
      classNames += " default-renderer";
    }
    // </stringsAreClassNames>

    let rowId = this.getRowIdFromIndex(rowIndex);
    let selectionClass = this.getRowHeaderSelectionClass(rowId);
    if (selectionClass) {
      classNames += selectionClass;
    }
    let cellContent;
    if (this.props.rowHeaderCellRenderer) {
      // User-supplied renderer. Note that props coming in haven't yet been adjusted for rowOffset
      props.rowIndex -= rowOffset;
      cellContent = this.props.rowHeaderCellRenderer(props);
      props.rowIndex += rowOffset;
    } else {
      let rowNum = rowIndex + 1;
      cellContent = rowNum.toString();
    }
    let dgCellProps = props;
    if (dgCellProps.classNames) {
      classNames += " " + dgCellProps.classNames;
    }
    let styles = props.style;
    if (dgCellProps.styles) {
      styles = {
        ...props.style,
        ...dgCellProps.styles
      };
    }

    // NOTE: The handler for onMouseDown calls setFocusInGrid, but it's too early (onMouseDown comes before onClick).
    // So we must also call setFocusInGrid for onClick...

    return /*#__PURE__*/React.createElement("div", _extends({
      key: props.key,
      className: classNames,
      style: styles,
      "data-cell-row-index": rowIndex,
      "data-test-id": `row-header-${rowIndex}`,
      onMouseDown: this.onRowHeaderCellMouseDown.bind(this, rowIndex),
      onClick: this.setFocusInGrid.bind(this)
    }, dgCellProps.eventHandlers), cellContent);
  }

  /**
   * Column header renderer.
   * @param {} props
   * @returns {any}
   */
  columnHeaderCellRenderer(props) {
    let columnOffset = this.props.showRowHeaderColumn ? 1 : 0;
    let columnIndex = props.columnIndex - columnOffset;
    if (columnIndex >= this._visibleLeafColumns.length) {
      return /*#__PURE__*/React.createElement(OverscrollCell, props);
    }
    let dgCol = this.getColumnFromMultiGridIndicesForRender(props.rowIndex, columnIndex);
    if (dgCol == null) {
      // This means that the header cell we want to render is meaningless because it will be hidden behind a spanning grouped column.
      // I used to return null here, however, per CAFE-544, it seems there may be a bug in the way the MultiGrid reuses the cell cache.
      // So now I just return an empty div instead.
      return /*#__PURE__*/React.createElement("div", {
        key: props.key,
        style: props.style
      });
    }
    let self = this;
    let cellContent = null;

    // CAT-1051
    // Offset the columnIndex passed to the cell renderers
    props.columnIndex = props.columnIndex - columnOffset;
    if (dgCol.headerCellRenderer) {
      cellContent = dgCol.headerCellRenderer(props, dgCol);
    } else if (this.props.columnHeaderCellRenderer) {
      cellContent = this.props.columnHeaderCellRenderer(props, dgCol);
    } else if (dgCol.isLeafColumn && dgCol.allowSort && dgCol.label && this.props.onSort) {
      if (this.props.sortBy === dgCol.id && this.props.sortDirection) {
        const spanRef = /*#__PURE__*/React.createRef();
        cellContent = /*#__PURE__*/React.createElement("div", {
          className: "ab-sortable-column-header"
        }, /*#__PURE__*/React.createElement(AbWithTooltip, {
          clippedTooltip: dgCol.label,
          clippedTrigger: spanRef
        }, /*#__PURE__*/React.createElement("span", {
          className: "ab-sortable-column-label abweb-label-end-truncation",
          ref: spanRef
        }, dgCol.label)), /*#__PURE__*/React.createElement("div", {
          onClick: onStopEventPropagation
        }, DataGrid.renderColumnHeaderSortImage(dgCol, this.props.onSort, this.props.sortDirection, this.props.onClearSort)));
      } else {
        const spanRef = /*#__PURE__*/React.createRef();
        cellContent = /*#__PURE__*/React.createElement("div", {
          className: "ab-sortable-column-header"
        }, /*#__PURE__*/React.createElement(AbWithTooltip, {
          clippedTooltip: dgCol.label,
          clippedTrigger: spanRef
        }, /*#__PURE__*/React.createElement("span", {
          className: "ab-sortable-column-label abweb-label-end-truncation",
          ref: spanRef
        }, dgCol.label)), /*#__PURE__*/React.createElement("div", {
          onClick: onStopEventPropagation
        }, DataGrid.renderColumnHeaderSortImage(dgCol, this.props.onSort, undefined, this.props.onClearSort)));
      }
    } else if (dgCol.label) {
      if (this.isFixedRowHeight) {
        const spanRef = /*#__PURE__*/React.createRef();
        // Fixed row height, so use ellipsis
        cellContent = /*#__PURE__*/React.createElement(AbWithTooltip, {
          clippedTooltip: dgCol.label,
          clippedTrigger: spanRef
        }, /*#__PURE__*/React.createElement("span", {
          className: "abweb-label-end-truncation",
          ref: spanRef
        }, dgCol.label));
      } else {
        // Variable row height, so label should wrap
        cellContent = dgCol.label;
      }
    }

    // Restore back to the proper columnIndex
    props.columnIndex = props.columnIndex + columnOffset;
    let clickHandler;
    if (this.selectionMgr.canSelectColumns) {
      clickHandler = this.onColumnHeaderCellClick.bind(this, dgCol.id);
    }
    let colId = dgCol.id;

    // <stringsAreClassNames>
    let classNames = "ab-column-header-cell";
    if (columnIndex === this.frozenColumnCount - 1) {
      // We are rendering locked columns and this is the last locked column
      classNames += " ab-last-frozen-column-cell";
    }
    if (dgCol.headerCellRenderer == null && this.props.columnHeaderCellRenderer == null) {
      classNames += " default-renderer";
    }
    if (this.isFixedRowHeight) {
      classNames += " grid-no-wrap-cell";
    }
    if (this.selectionMgr.canSelect) {
      classNames += " ab-select-mode";
    }
    // </stringsAreClassNames>

    let cssAdjustments = {};
    let selectionClass = this.getColumnHeaderSelectionClass(colId);
    if (selectionClass) {
      classNames += selectionClass;
      // Prevent header css from highlighting selected column headers
      cssAdjustments.backgroundColor = undefined;
      cssAdjustments.color = undefined;
    }
    let dgCellProps = props;
    if (dgCellProps.classNames) {
      classNames += " " + dgCellProps.classNames;
    }
    if (dgCol.headerClassNames) {
      classNames += ` ${dgCol.headerClassNames}`;
    }
    if (this.haveGroupedColumns && props.rowIndex < this._fixedRowCount - 1) {
      // We are rendering a grouped column header so adjust its size
      cssAdjustments.width = this.getColumnCurrentWidth(dgCol);
    } else if (this.cellMeasurerCache) {
      // We are using a <CellMeasurer> for variable-height rows which requires explicit width to be set
      // but note that use of getColumnWidth() requires absolute colIndex so we have to add columnOffset back in
      cssAdjustments.width = this.getColumnWidth({
        index: props.columnIndex
      });
    } else {
      // comment
    }
    let styles = props.style;
    if (dgCol.headerStyles || dgCellProps.styles || cssAdjustments || columnIndex < this.frozenColumnCount && this.fixedRowCount > 1) {
      // NB: We also check for frozenColumnCount because we need to mutate the styles.height below
      styles = {
        ...dgCol.headerStyles,
        ...props.style,
        ...dgCellProps.styles,
        ...cssAdjustments
      };
    }
    if (columnIndex < this.frozenColumnCount && this.fixedRowCount > 1 && typeof styles.height === "number") {
      // We are rendering column header cells in the frozen columns of the topLeftGrid AND we have grouped columns
      // In this case, the styles.height isn't computed correctly so we will tweak it here
      const mgr = props.parent._rowSizeAndPositionManager;
      if (mgr) {
        styles.height = Math.max(styles.height, mgr.getTotalSize());
      }
    }
    let colResizer = null;
    const leafCols = dgCol.getAllLeafColumns(true);
    const resizeColTarget = leafCols.length > 0 ? leafCols[leafCols.length - 1] : dgCol;
    if (this.props.allowColumnResize && resizeColTarget.allowResize) {
      // Add sizer div to provide affordance for user column resize
      let mouseDownHandler = this.onMouseDownColumnResizerHandler.bind(this, resizeColTarget);
      colResizer = /*#__PURE__*/React.createElement("div", _extends({
        className: "ab-column-resizer-divider",
        "data-test-component": "ab-column-resizer",
        onMouseDown: mouseDownHandler
      }, dgCol?.headerDividerDragDropEventHandlers));
    }
    let leftColumnDivider = dgCol?.leftHeaderDividerDragDropEventHandlers === undefined ? null : /*#__PURE__*/React.createElement("div", _extends({
      className: "ab-column-left-column-divider"
    }, dgCol?.leftHeaderDividerDragDropEventHandlers));

    // Update cached column position info for client use
    let c = dgCol;
    c._top = Number(styles.top);
    c._left = Number(styles.left);
    c._height = Number(styles.height);
    const canDrag = !!this.props.allowColumnDragToReorder && !this.isColumnFrozen(dgCol);
    return /*#__PURE__*/React.createElement("div", {
      key: props.key,
      className: classNames,
      style: styles,
      "data-cell-id": dgCol.id,
      "data-cell-row-index": props.rowIndex,
      "data-cell-column-index": columnIndex,
      "data-test-id": `header-col-${columnIndex}-row-${props.rowIndex}`,
      onClick: clickHandler,
      draggable: canDrag,
      onDragStart: canDrag ? onDragStart : undefined,
      onDragEnd: canDrag ? onDragEnd : undefined
    }, leftColumnDivider, /*#__PURE__*/React.createElement("div", {
      className: "header-cell-content"
    }, cellContent), colResizer);
    function onStopEventPropagation(event) {
      // We wrap the sort glyph in a wrapper div to stop propagation of the click event
      // because we don't want the click on the menu to affect selection
      event.stopPropagation();
    }
    function onDragStart(event) {
      self.onDragStartColumn(event, dgCol);
    }
    function onDragEnd(event) {
      let ghostEl = document.getElementById("dataGridColumnDragImage");
      if (ghostEl) {
        document.body.removeChild(ghostEl);
      }
      DataGrid.LOGGER.info(`columnHeaderCellRenderer.onDragEnd Column drag is complete`);
      self.setState({
        columnDragInfo: null
      });
    }
  }
  onDragStartColumn(event, column) {
    // event.preventDefault();
    event.dataTransfer.dropEffect = "move";

    // Figure out if we have multiple columns selected and if they are valid
    let columnsToMove = this.getSelectedColumnRangeForDrag(column);
    let dragSourceLabel = columnsToMove.map(col => col.label).join(" | ");
    event.dataTransfer.setData("dataGridColumnMove", columnsToMove.map(col => col.id).join("||"));
    let xOffset = (dragSourceLabel.length * 8 + 20) / 2;
    let ghostEl = document.createElement("div");
    ghostEl.id = "dataGridColumnDragImage";
    ghostEl.className = "data-grid-column-drag-image";
    ghostEl.innerHTML = dragSourceLabel;
    ghostEl.style.position = "absolute";
    ghostEl.style.top = "-1000px";
    ghostEl.style.left = "-1000px";
    ghostEl.style.pointerEvents = "none";
    ghostEl.style.zIndex = "15000";
    document.body.appendChild(ghostEl);
    event.dataTransfer.setDragImage(ghostEl, xOffset, 30);

    // Seems that the Drag/drop gets cancelled if I call setState() directly in this event handler
    setTimeout(() => this.setState({
      columnDragInfo: columnsToMove
    }), 10);
  }

  /**
   * Helper method that returns a contiguous range of columns that can be dragged for reorder.
   * A number of constraints must be met:
   * 1) Only columns may be selected.
   * 2) All selected columns must be contiguous
   * 3) All columns must share the same parent column (or null if at top level).
   * @param column Column starting the drag.
   * @return Array of draggable columns or if constraints are not met, will be an Array of length 1 with column being the Array value.
   */
  getSelectedColumnRangeForDrag(column) {
    let selectedItems = this.selectionMgr.getSelectedItems();
    let selectedColumns = [];
    for (let selectedItem of selectedItems) {
      if (selectedItem.isColumn) {
        let col = DataGridColumn.findColumnById(selectedItem.colId, this.currentColumns);
        if (col && !this.isColumnFrozen(col)) {
          selectedColumns.push(col);
        }
      } else {
        // We have a mix of selected item types so we failed validation
        return [column];
      }
    }
    if (selectedColumns.length < 2) {
      return [column];
    }

    // Here is a fun fact. If a grouped column is selected, it is in the selectedItems list and we know that it must include all visible descendants too,
    // so we will prune these descendants from selectedColumns
    let columnSet = new Set(selectedColumns);
    for (let col of selectedColumns) {
      if (col.childColumnCount > 0) {
        // This is a grouped column
        let descendants = col.getAllLeafColumns(true);
        descendants.forEach(c => columnSet.delete(c));
      }
    }

    // columnSet should now contain only selected grouped columns and other columns
    selectedColumns = Array.from(columnSet);

    // More validation to make sure each selectedColumns have the same parent
    let parentCol = selectedColumns[0].parentColumn;
    for (let n = 1; n < selectedColumns.length; n++) {
      if (selectedColumns[n].parentColumn !== parentCol) {
        // Failed validation
        return [column];
      }
    }

    // Now verify that the columns are contiguous visible columns
    let visibleColumns = parentCol ? parentCol.getVisibleChildColumns() : this.currentColumns.filter(col => col.visible);
    selectedColumns.sort((col1, col2) => {
      let index1 = visibleColumns.indexOf(col1);
      let index2 = visibleColumns.indexOf(col2);
      return index1 - index2;
    });

    // Now verify that these ascending indices are contiguous
    let firstIndex = visibleColumns.indexOf(selectedColumns[0]);
    let rest = visibleColumns.slice(firstIndex, firstIndex + selectedColumns.length);
    for (let n = 0; n < selectedColumns.length; n++) {
      if (selectedColumns[n] !== rest[n]) {
        // Non contiguous
        return [column];
      }
    }

    // We are good to go!
    return selectedColumns;
  }

  /**
   * Helper function used for rendering column headers in MultiGrid.
   * This method gets the relevant DataGridColumn.
   * @param {number} rowIndex
   * @param {number} columnIndex Zero-based column index in the visibleLeafColumns array.
   * @returns {DataGridColumn} but will return null if not at the right slot
   */
  getColumnFromMultiGridIndicesForRender(rowIndex, columnIndex) {
    let col = this._visibleLeafColumns[columnIndex];
    while (col != null) {
      if (col.cellSlot.rowIndex === rowIndex && col.cellSlot.columnIndex === columnIndex) {
        return col;
      }
      if (rowIndex > col.cellSlot.rowIndex) {
        return null;
      }
      col = col.parentColumn;
    }
    return null;
  }
  onBodyCellMouseDown(rowIndex, columnIndex, event) {
    // If we aren't doing a left click, ignore it
    // event.button is equal to 0 when it is a left click
    // so anything non 0 is bad
    if (event.button) {
      return;
    }
    let rowId = this.getRowIdFromIndex(rowIndex);
    let colId = this.getColumnIdFromIndex(columnIndex);
    let {
      editingMachine
    } = this.props;
    if (editingMachine && editingMachine.currentEditCell && editingMachine.currentEditCell.areEqual(new SelectedItem(rowId, colId))) {
      // We are currently editing, and user clicked in cell being edited,
      // so ignore this click
      return;
    }
    this.selectionMgr.onClickCell(rowId, colId, event.shiftKey, event.ctrlKey && !this.selectionMgr.isRectangularSelectionMode);
    if (this.selectionMgr.isMultipleSelectionMode) {
      this._selectionDragUnderway = true;
      let tracker = new MouseMoveTracker(event.target);
      tracker.track(event.nativeEvent, () => null, () => {
        this._selectionDragUnderway = false;
      }, undefined, () => {
        this._selectionDragUnderway = false;
      });
    }
  }
  onBodyCellMouseEnter(rowIndex, columnIndex) {
    if (this._selectionDragUnderway) {
      let rowId = this.getRowIdFromIndex(rowIndex);
      let colId = this.getColumnIdFromIndex(columnIndex);
      if (this.selectionMgr.canSelectCells) {
        this.selectionMgr.extendCellSelection(rowId, colId);
      } else {
        this.selectionMgr.extendRowSelection(rowId);
      }
    }
  }
  onBodyCellDoubleClick(rowIndex, columnIndex) {
    let rowId = this.getRowIdFromIndex(rowIndex);
    let colId = this.getColumnIdFromIndex(columnIndex);
    this.editCell(rowId, colId);
  }
  onRowHeaderCellMouseDown(rowIndex, event) {
    // If we aren't doing a left click, ignore it
    // event.button is equal to 0 when it is a left click
    // so anything non 0 is bad
    if (event.button) {
      return;
    }
    let rowId = this.getRowIdFromIndex(rowIndex);
    this.selectionMgr.onClickRowHeader(rowId, event.shiftKey, event.ctrlKey && !this.selectionMgr.isRectangularSelectionMode);
    this.setFocusInGrid();
  }
  onColumnHeaderCellClick(colId, event) {
    this.selectionMgr.onClickColumnHeader(colId, event.shiftKey, event.ctrlKey && !this.selectionMgr.isRectangularSelectionMode);
    this.setFocusInGrid();
  }

  // private parseKey(key: string)
  // {
  //   // Assumes the default react-virtualized Grid format 'rowIndex-columnIndex'
  //   let values = key.split("-");
  //   return {rowIndex: parseInt(values[0], 10), columnIndex: parseInt(values[1], 10)};
  // }

  onSelectionChangeInternal() {
    this.refresh(false);
    if (this.props.scrollAnchor) {
      if (this.props.scrollAnchor === "auto") {
        this.updateAnchorPosition(this.lastSelectedItem());
      }
    }
    if (this.props.onSelectionChange) {
      this.props.onSelectionChange(this.selectionMgr.selectionMachine);
    }
  }

  /**
   * Gets the DataGridColumn.id for the leaf column the columnIndex
   * @param {number} columnIndex 0-based body column leaf index
   * @returns {string}
   */
  getColumnIdFromIndex(columnIndex) {
    // Right now, this is a straightforward mapping, but once we support
    // column hiding and possibly reorder, we'll need a different data structure
    if (columnIndex >= 0 && columnIndex < this._visibleLeafColumns.length) {
      return this._visibleLeafColumns[columnIndex].id;
    }
    return "";
  }

  /**
   * Gets the zero-based column index from a column id.
   * This assumes the colId is associated with a leaf column
   * @param {ColId} colId
   * @returns {number}
   */
  getColumnIndexFromId(colId) {
    // Right now this is straightforward, but once we support
    // column hiding and possibly reorder, we'll need a different data structure
    if (colId == null) {
      return -1;
    }
    let index = this.visibleLeafColumnMap.get(colId);
    if (index == null) {
      return -1;
    } else {
      return index;
    }
  }

  /**
   * Gets the DataGridColumn corresponding to the leaf column index.
   * @param {number} columnIndex 0-based visible leaf column index.
   * @returns {DataGridColumn | null}
   */
  getColumnFromIndex(columnIndex) {
    let id = this.getColumnIdFromIndex(columnIndex);
    if (id === undefined) {
      return null;
    }
    return this.getColumnFromId(id);
  }

  /**
   * Gets the DataGridColumn associated from a colId.
   * @param {ColId} colId
   * @returns {DataGridColumn | null}
   */
  getColumnFromId(colId) {
    if (colId == null) {
      return null;
    }
    return this.columnMap.get(colId) || null;
  }
  getRowIndexFromId(rowId) {
    if (this.props.rowIdToIndexMapper) {
      return this.props.rowIdToIndexMapper(rowId);
    }
    return rowId;
  }
  getRowIdFromIndex(index) {
    if (index < 0 || index >= this.props.rowCount) {
      return null;
    }
    if (this.props.rowIndexToIdMapper) {
      return this.props.rowIndexToIdMapper(index);
    }
    return index;
  }

  /**
   * Forces the grid(s) to rerender cells.
   * hardRefresh is used to force every cell in the grid
   * to rerender itself. A soft refresh will cause most cells
   * not to rerender, unless they had a style or classname change.
   * (mostly for the case of changing selection)
   * Be wary that a soft refresh will not respond to changes
   * in classnames that would happen due to manipulating the
   * DataGridCellProps object in a cell renderer because the
   * render cycle is short circuited before that cell renderer
   * is called.
   */
  refresh(hardRefresh = true) {
    if (this.multiGrid) {
      if (hardRefresh) {
        this._refreshCount++;
      }
      this.multiGrid.forceUpdateGrids();
    } else if (this.grid) {
      if (hardRefresh) {
        this._refreshCount++;
      }
      this.grid.forceUpdate();
    }
  }

  /**
   * A very hacky way to make the Grid rerender.
   * This hammer is heavier than the refresh() method, but not as heavy as
   * recalculateGridSize().
   * It is being used to support deferred rendering when scrolling.
   */
  refreshMultiGridScrollHack() {
    let grid = this.multiGrid || this.bodyGrid;
    if (grid == null) {
      return;
    }
    if (this.props.rowCount === 0) {
      // Per MH-21626 attempt to avoid weirdness in HSB when there are no rows in the main grid
      return;
    }
    let scrollInfo = {
      scrollLeft: grid.state.scrollLeft,
      scrollTop: grid.state.scrollTop
    };
    scrollInfo.scrollTop++;
    grid.setState(scrollInfo);
    scrollInfo.scrollTop--;
    grid.setState(scrollInfo);
  }
  onTopLeftCellClick(event) {
    if (!this.selectionMgr.isMultipleSelectionMode) {
      return;
    }
    if (event.ctrlKey) {
      this.selectionMgr.clear();
    } else {
      this.selectionMgr.selectAll();
      this.setFocusInGrid();
    }
  }

  /**
   * Scroll the last row into view
   */
  forceScrollToBottom() {
    const rowId = this.getRowIdFromIndex(this.props.rowCount - 1);
    this.scrollToCell({
      rowId
    });
  }
  onScroll(params) {
    this._scrollParams = params;
    if (this.props.onScroll) {
      this.props.onScroll(params);
    }
    if (this.props.scrollAnchor) {
      if (this.props.scrollAnchor === "auto") {
        this.updateAnchorPosition(this.lastSelectedItem());
      } else {
        this.updateAnchorPosition(this.props.scrollAnchor);
      }
    }
    if (this.scrollToBottomHelper) {
      this.scrollToBottomHelper.gridScrolled(params.scrollTop);
    }
  }

  /**
   * Gets the ScrollParams representing the scroll positions for the grid.
   * @returns {}
   */
  get scrollParams() {
    return this._scrollParams;
  }

  /**
   * Gets width of vertical scroll bar on body grid (if present).
   * Will be 0 if no vertical scroll bar is present.
   */
  get verticalScrollBarSize() {
    return this._verticalScrollBarSize;
  }
  onSectionRendered(params) {
    this._sectionRenderedParams = params;
    this.computeIsAnchorVisible();
    this.props.onSectionRendered && this.props.onSectionRendered(params);
  }
  beforeSectionRendered(params) {
    this.props.beforeSectionRendered && this.props.beforeSectionRendered(params);
  }
  computeIsAnchorVisible() {
    const {
      anchorCell
    } = this.state;
    this._isAnchorVisible = anchorCell !== undefined && this.isCellInViewPort({
      rowId: anchorCell.row?.id,
      colId: anchorCell.col?.id
    });
  }
  updateAnchorPosition(anchor) {
    if (!anchor) {
      if (this.state.anchorCell !== undefined) {
        this.clearAnchorCell();
      }
      return;
    }
    let row;
    let col;
    let anchorCell = this.state.anchorCell;
    if (anchor.rowId !== null && anchor.rowId !== undefined) {
      let index = this.getRowIndexFromId(anchor.rowId);
      if (index === -1) {
        // The row is no longer valid - clear the anchor cell
        this.clearAnchorCell();
        return;
      } else if (!anchorCell || !anchorCell.row || index !== anchorCell.row.index || anchor.rowId !== anchorCell.row.id) {
        row = {
          id: anchor.rowId,
          index: index
        };
      }
    }
    if (anchor.colId !== null && anchor.colId !== undefined) {
      let index = this.getColumnIndexFromId(anchor.colId);
      if (index === -1) {
        // The column is no longer valid - clear the anchor cell
        this.clearAnchorCell();
        return;
      } else if (!anchorCell || !anchorCell.col || index !== anchorCell.col.index || anchor.colId !== anchorCell.col.id) {
        col = {
          id: anchor.colId,
          index: index
        };
      }
    }
    if (row || col) {
      anchorCell = anchorCell || {};
      if (row || !anchor.rowId) {
        anchorCell = {
          ...anchorCell,
          row
        };
      }
      if (col || !anchor.colId) {
        anchorCell = {
          ...anchorCell,
          col
        };
      }
      this.setAnchorCell(anchorCell);
    }
  }
  get sectionRenderedParams() {
    return this._sectionRenderedParams;
  }
  onKeyDownHandler(event) {
    // DataGrid.LOGGER.info("onKeyDownHandler key: " + event.key);
    let eventHandled = false;
    if (this.props.onKeyDown) {
      eventHandled = this.props.onKeyDown(event);
    }
    if (!eventHandled) {
      this.keyboardMgr.onKeyDown(event);
    }
  }
  isCellInViewPort({
    rowId,
    colId
  }) {
    let minRow = this.sectionRenderedParams.rowStartIndex;
    let maxRow = this.sectionRenderedParams.rowStopIndex;
    let minCol = this.sectionRenderedParams.columnStartIndex;
    let maxCol = this.sectionRenderedParams.columnStopIndex;

    // If the row or col isn't specified then default to a visible row/col
    let rowIndex = minRow;
    if (rowId != null) {
      rowIndex = this.getRowIndexFromId(rowId);
    }
    let colIndex = minCol;
    if (colId != null) {
      colIndex = this.getColumnIndexFromId(colId);
    }
    let rowVisible = minRow <= rowIndex && maxRow >= rowIndex;
    let colVisible = minCol <= colIndex && maxCol >= colIndex;
    return rowVisible && colVisible;
  }

  /**
   * This function will scroll the grid to the cell, column, or row specified.
   * If the internal grid hasn't finished instantiating itself then the scroll will be delayed
   * until after the instantiation finishes.
   * @param {RowId} rowId
   * @param {ColId} colId
   * @param {DataGridCellAlignment} position
   */
  scrollToCell({
    rowId,
    colId
  }, position = DataGridCellAlignment.AUTO) {
    //console.log(`DataGrid scrollToCell ${rowId} ${colId}`); //`

    this.safelyScrollToCell({
      rowId,
      colId
    }, position);
  }
  safelyScrollToCell({
    rowId,
    colId
  }, position = DataGridCellAlignment.AUTO, measure = this.isVariableRowHeight) {
    if (this.bodyGrid) {
      let rowIndex;
      let colIndex;
      if (rowId !== undefined) {
        rowIndex = this.getRowIndexFromId(rowId);
      }
      if (colId !== undefined) {
        colIndex = this.getColumnIndexFromId(colId);
      }
      let cell = {
        rowIndex: rowIndex,
        columnIndex: colIndex
      };
      if (position !== DataGridCellAlignment.AUTO) {
        let newScrollPosition = this.bodyGrid.getOffsetForCell({
          alignment: position,
          ...cell
        });
        if (colId === undefined) {
          newScrollPosition.scrollLeft = undefined;
        }
        if (rowId === undefined) {
          newScrollPosition.scrollTop = undefined;
        }
        this.bodyGrid.scrollToPosition(newScrollPosition);
      } else {
        this.bodyGrid.scrollToCell(cell);
      }
      if (measure) {
        // In the case of variable row height the grid has to measure all of its cells before
        // we can calculate an accurate scroll top
        this.bodyGrid.forceUpdate(() => {
          // This call is necessary so that the grid makes sure that any cells that needed to be
          // measured in order to calculate the scroll top get a new set of styles that accurately
          // reflect the position that they are supposed to be in
          this.recomputeGridSize(false, () => {
            this.safelyScrollToCell({
              rowId,
              colId
            }, position, false);
          });
        });
      }
    } else {
      this._delayedScroll = [{
        rowId,
        colId
      }, position];
    }
  }

  /**
   * This method will update the scroll left and scroll top of the grid.
   * If the grid hasn't been rendered yet, this method is a noop.
   * @param {number} scrollLeft
   * @param {number} scrollTop
   */
  scrollToPosition({
    scrollLeft,
    scrollTop
  }) {
    //console.log(`DataGrid scrollToPosition ${scrollLeft} ${scrollTop}`); //`
    if (this.bodyGrid) {
      // any is needed to let the undefined through
      this.bodyGrid.scrollToPosition({
        scrollLeft,
        scrollTop
      });
    }
  }

  /**
   * This method will update the scroll left and scroll top of the grid.
   * If the internal grid hasn't finished instantiating itself then the scroll will be delayed
   * until after the instantiation finishes.
   * @param {number} scrollLeft
   * @param {number} scrollTop
   */
  safelyScrollToPosition({
    scrollLeft,
    scrollTop
  }) {
    //console.log(`DataGrid safelyScrollToPosition ${scrollLeft} ${scrollTop}`); //`
    if (this.bodyGrid) {
      // any is needed to let the undefined through
      this.bodyGrid.scrollToPosition({
        scrollLeft,
        scrollTop
      });
    } else {
      this._delayedScrollPosition = {
        scrollLeft,
        scrollTop
      };
    }
  }
  lastSelectedItem() {
    // use this so that the items don't get sorted by row index
    let selectionRegion = this.selectionMgr.selectionRegion;
    const mostRecentlySelectedItem = selectionRegion.anchorItem2 ? selectionRegion.anchorItem2 : selectionRegion.anchorItem ? selectionRegion.anchorItem : undefined;
    return mostRecentlySelectedItem;
  }

  /**
   * Clears the currently-stored anchor cell
   */
  clearAnchorCell() {
    this.setAnchorCell();
  }

  /**
   * Sets the current anchor cell
   */
  setAnchorCell(anchorCell) {
    let {
      row,
      col
    } = anchorCell || {};
    if (row && row.id !== null && row.index === -1) {
      row = undefined;
    }
    if (col && col.id !== null && col.index === -1) {
      col = undefined;
    }
    const newAnchorCell = row || col ? {
      row,
      col
    } : undefined;
    if (!ObjectUtils.deepCompare(newAnchorCell, this.state.anchorCell)) {
      this.setState({
        anchorCell: newAnchorCell
      });
    }
  }
  scrollToAnchorCell() {
    const anchorRowId = this.state.anchorCell?.row?.id || -1;
    if (anchorRowId !== -1 && this.bodyGrid && this._keepAnchorVisible) {
      /*
      The anchorCell has both a row id and index, but it is possible for the row index to be
      stale.  This is because the call to setState that updates the anchor cell is asynchronous
      If we have an anchor cell, but the row index of that anchor is -1, then that row has
      probably been deleted.
       */
      const index = this.getRowIndexFromId(anchorRowId);
      if (index >= 0) {
        const anchorColId = this.state.anchorCell?.col?.id;
        this.scrollToCell({
          rowId: anchorRowId,
          colId: anchorColId
        });
      }
    }
    this._isScrollingToAnchor = false;
  }

  /**
   * Puts focus in main Grid.
   */
  setFocusInGrid() {
    let g = this.bodyGrid;
    if (g && g._scrollingContainer) {
      // console.log(`DataGrid setFocusIn ${g._scrollingContainer}`); //`

      // This focuses the grid while preventing the browser from auto-scrolling to the grid when setting focus.
      // The auto-scroll can be jarring and unexpected for the user.
      g._scrollingContainer.focus({
        preventScroll: true
      });
    }
  }
  getBodyGrid() {
    return this.bodyGrid;
  }
  getAltRowClassName() {
    if (this.props.altRowClassName === undefined) {
      return "ab-alt-row";
    } else if (this.props.altRowClassName === null) {
      return null;
    } else {
      return this.props.altRowClassName;
    }
  }
  overscanIndicesGetter(params) {
    let {
      startIndex,
      stopIndex,
      cellCount,
      overscanCellsCount,
      direction
    } = params;
    let overscanStart = startIndex - overscanCellsCount;
    let overscanStop = stopIndex + overscanCellsCount;

    // cap overscan to reduce reflows, always round to a 5
    if (direction === "vertical") {
      const overscanRowCount = overscanCellsCount;
      const overscanRound = 10;
      overscanStart = startIndex - overscanRowCount;
      overscanStop = stopIndex + overscanRowCount;
      overscanStart = overscanStart - overscanStart % overscanRound;
      overscanStop = overscanStop + overscanRound - overscanStop % overscanRound;
    }
    return {
      overscanStartIndex: Math.max(0, overscanStart),
      overscanStopIndex: Math.min(cellCount - 1, overscanStop)
    };
  }

  /**
   * Called after rendering to see if a VSB has been added/removed which might cause us to recalculate column widths.
   * Note that I can't trust the onScrollbarPresenceChange() callback because it isn't correct for the MultiGrid case.
   */
  postRenderFixups() {
    let bg = this.bodyGrid;
    if (bg == null) {
      DataGrid.LOGGER.debug("postRenderFixups bodyGrid = null");
      return false;
    }
    let w = bg._verticalScrollBarSize;
    let delta = this._verticalScrollBarSize - w;
    if (delta !== 0) {
      this._verticalScrollBarSize = w;
      if (this.inColumnFitMode) {
        // We are fitting columns, so we have to do a full render pass to recompute colWidths
        DataGrid.LOGGER.debug(`postRenderFixups Calling forceUpdate() and recomputeGridSize()`);

        // MH-8160: Seems that forceUpdate() may now be async, so wait until we've done our render() pass
        this.forceUpdate(() => this.recomputeGridSize(true));
        return true;
      }
    }
    return false;
  }

  /**
   * Called from componentWillUpdate() to see if we need to hit the Grid with recomputeGridSize().
   * @param {Readonly<DataGridProps>} nextProps
   * @returns {boolean}
   */
  gridSizeNeedsRecompute(nextProps) {
    // See https://github.com/bvaughn/react-virtualized/issues/136 for motivation here
    if (this.inColumnFitMode && nextProps.width !== this.props.width) {
      DataGrid.LOGGER.debug(`gridSizeNeedsRecompute inColumnFitMode width: ${this.props.width} new width: ${nextProps.width}`);
      return true;
    }
    let nextLeafColumns = DataGridColumn.getLeafColumns(nextProps.columns, true);
    if (nextLeafColumns.length !== this._visibleLeafColumns.length) {
      DataGrid.LOGGER.debug(`gridSizeNeedsRecompute columns.length changed from ${this._visibleLeafColumns.length} to ${nextLeafColumns.length}`);
      return true;
    }
    for (let n = 0; n < nextLeafColumns.length; n++) {
      let nextCol = nextLeafColumns[n];
      let col = this._visibleLeafColumns[n];
      if (nextCol.id !== col.id) {
        // Maybe columns have moved
        return true;
      }
      if (nextCol.widthPercent !== col.widthPercent) {
        DataGrid.LOGGER.debug(`gridSizeNeedsRecompute ${n}: widthPercent changed from ${col.widthPercent} to ${nextCol.widthPercent}`);
        return true;
      }
      if (nextCol.width !== col.width) {
        DataGrid.LOGGER.debug(`gridSizeNeedsRecompute ${n}: width changed from ${col.width} to ${nextCol.width}`);
        return true;
      }
    }
    return false;
  }
  get selectionMgr() {
    return this._selectionMgr;
  }
  onMouseDownColumnResizerHandler(column, event) {
    DataGrid.LOGGER.debug("onMouseDownColumnResizerHandler id: " + column.id);
    let target = event.target;
    target = target.closest("div.ab-column-header-cell");
    if (target == null) {
      return;
    }
    target = target.parentElement;
    if (target == null) {
      return;
    }
    let divCell = target.querySelector(`div.ab-column-header-cell[data-cell-id='${column.id}']`);
    if (divCell == null) {
      return;
    }
    this.columnResizeMgr.resizeColumn(column, divCell, event);
  }
  onColumnResizeComplete(column, newWidth) {
    if (this.getColumnCurrentWidth(column) === newWidth) {
      // Nothing to do
      return;
    }
    DataGrid.LOGGER.debug(`onColumnResizeComplete newWidth: ${newWidth}`);
    if (this.isColumnFrozen(column)) {
      // Edge case validation when changing width of a frozen column
      let usableWidth = this.innerGridWidth - this.verticalScrollBarSize;
      if (this.props.showRowHeaderColumn && this.props.rowHeaderWidth !== undefined) {
        usableWidth -= this.props.rowHeaderWidth;
      }

      // and leave a little bit for the other columns
      usableWidth -= 20;

      // Now sum the widths of all frozen columns except the one we are resizing
      let frozenColumnWidth = 0;
      for (let n = 0; n < this.frozenColumnCount; n++) {
        let col = this.currentColumns[n];
        if (col !== column) {
          frozenColumnWidth += col.actualWidth;
        }
      }
      if (frozenColumnWidth + newWidth > usableWidth) {
        // If we allow this, we won't have any usable grid area
        // so modify it downward
        newWidth = usableWidth - frozenColumnWidth;
        DataGrid.LOGGER.debug(`onColumnResizeComplete Adjusting newWidth down because of viewport constraints. newWidth: ${newWidth}`);
      }
    }
    if (this.props.columnResizeMode === ColumnResizeMode.TRANSITION_TO_FIXED_WIDTHS) {
      // Since user wants TRANSITION_TO_FIXED_WIDTHS, this means that from now on, we will have fixed widths
      this.isFixedWidthMode = true;
    }
    this.setColumnUserWidth(column, newWidth);
    if (this.isVariableRowHeight && this.cellMeasurerCache) {
      const scrollToRowId = this.getColumnResizeScrollTargetRowId();

      // Clear the cellMeasurerCache and remeasure everything
      // let scrollInfo = {scrollLeft: this.multiGrid!.state.scrollLeft, scrollTop: this.multiGrid!.state.scrollTop};

      this.updateColumnWidths(this.innerGridWidth);
      this.cellMeasurerCache.clearAll();

      // ??? I don't know what the correct incantations are to make this work...
      this.recomputeGridSize(false, () => {
        if (scrollToRowId) {
          if (!this.isCellInViewPort({
            rowId: scrollToRowId
          })) {
            this.scrollToCell({
              rowId: scrollToRowId
            }, DataGridCellAlignment.START);
          }
        }
      });

      /*
      HACK ALERT: It seems that nothing seems to work right, but I found that if I just scroll a little
      bit everything gets re-rendered properly. This hack forces a scroll to simulate this case.
       */
      // scrollInfo.scrollTop++;
      // this.multiGrid!.setState(scrollInfo);
      // scrollInfo.scrollTop--;
      // this.multiGrid!.setState(scrollInfo);
    } else {
      this.updateColumnWidths(this.innerGridWidth);
      this.recomputeGridSize(true);
    }
    if (this.props.onColumnWidthChange) {
      this.props.onColumnWidthChange(column, newWidth);
    }
  }

  /**
   * Determine the row we should scroll to after a column resize
   * The goal is to keep either the current anchor in view or to keep
   * the viewport showing the same first row
   */
  getColumnResizeScrollTargetRowId() {
    if (!this.sectionRenderedParams) {
      return null;
    }
    const rowId = this.state.anchorCell?.row?.id;
    return rowId !== undefined && this.isCellInViewPort({
      rowId
    }) ? rowId : this.getRowIdFromIndex(this.sectionRenderedParams.rowStartIndex);
  }

  /**
   * Forces the Grid/MultiGrid to recompute its sizes.
   * You may need to call this if you are using variable-height rows if any changes to your columns or data
   * could result in a grid cell to have a different height. This includes column resizing operations.
   * @param {boolean | (SelectedItem | RowId)[]} invalidateCellHeightCache Parameter to control how the cellMeasurerCache has to be pruned.
   * If false is passed, the cache is not cleared.
   * If true is passed, the cellMeasurerCache is completely cleared.
   * If an Array of SelectedItems is passed, only the portions of the cache corresponding to each SelectedItem is cleared.
   * That is, if a row SelectedItem is included, only the cells in the cache for that row are cleared.
   * If a cell SelectedItem is included, only that cell is cleared, etc.
   * This gives you a finer-grained control over how the cache is cleared which may improve scroll jumps in your grid.
   * Note that if one of the Array items is a RowId itself, it is interpreted to mean clear the cache from that row
   * to the end of the cached data. This is useful, for example, if you are inserting or removed a row somewhere
   * in the middle of your data rows since this operation will require that you clear the cache from that row on down.
   * @param {() => void} cb Optional callback to be called when the contained Grid/MultiGrid has been updated.
   */
  recomputeGridSize(invalidateCellHeightCache = false, cb) {
    if (this.visibilityWatcher?.enable() === false) {
      // The grid is hidden so there is no point in doing any row height measurements in this state
      DataGrid.LOGGER.info("recomputeGridSize DataGrid is hidden so deferring row height calculations");
      return;
    }
    if (invalidateCellHeightCache !== false) {
      if (invalidateCellHeightCache === true) {
        this.clearCellMeasurerCache(null);
        this._refreshCount++;
      } else {
        // Array arg
        this.clearCellMeasurerCache(invalidateCellHeightCache);
      }
    } else {
      // false passed so just invalidate the grid renderers
      this._refreshCount++;
    }
    let g = this.grid || this.multiGrid;
    if (g) {
      g.recomputeGridSize();
      g.forceUpdate(() => {
        if (cb) {
          cb();
        }
      });
    }
  }

  /**
   * Called from recomputeGridSize() to explicitly clears items from the CellMeasurerCache.
   * @param {(SelectedItem | RowId)[] | null} items Note that null clears the whole cache.
   * Passing a RowId by itself (not a SelectedItem) is interpreted to mean clear all items at that RowId to the end.
   */
  clearCellMeasurerCache(items) {
    // Delegate to utils class
    CellMeasurerCacheUtils.clearItems(items, this.cellMeasurementFreshness, this);
  }

  /**
   * Called to recompute the DataGrid border size.
   * @returns {boolean} true if a re-render is necessary, false otherwise.
   */
  recomputeGridBorderSize() {
    if (this.containerDiv && this.containerDiv.offsetWidth > 0 && this.containerDiv.offsetHeight > 0 && this.props.width > 0 && this.props.height > 0) {
      let widthDiff = this.containerDiv.offsetWidth - this.props.width;
      let heightDiff = this.containerDiv.offsetHeight - this.props.height;
      if (heightDiff !== 0 || widthDiff !== 0) {
        this.setState({
          borderXWidth: this.state.borderXWidth + widthDiff,
          borderYWidth: this.state.borderYWidth + heightDiff
        }, () => this.recomputeGridSize());
        return true;
      }
    }
    return false;
  }
  get isVariableRowHeight() {
    return this.state.rowHeight === undefined || typeof this.state.rowHeight === "function";
  }
  get isFixedRowHeight() {
    return !this.isVariableRowHeight;
  }
  get focusCell() {
    return this.selectionMgr.focusCell;
  }

  /**
   * Edit a cell.
   * This is an intent to edit.
   * Note that this is equivalent to calling editMachine.editCell().
   * @param {RowId} rowId
   * @param {ColId} colId
   * @returns {boolean} true if we are editing, or false if editing is not allowed.
   */
  editCell(rowId, colId) {
    this.endEdit(false);
    if (!this.editable) {
      return false;
    }
    let editingMachine = this.props.editingMachine;
    let column = this.getColumnFromId(colId);
    if (column == null || !column.editable) {
      DataGrid.LOGGER.info("editCell Cannot edit because column is not editable");
      return false;
    }
    if (!this.selectionMgr.canSelectCells) {
      DataGrid.LOGGER.info("editCell Cannot edit because DataGrid.selectionMode cannot select cells");
      return false;
    }
    if (!editingMachine.onBeforeEdit(rowId, colId, column)) {
      return false;
    }

    // We are good to go
    let focusCell = this.focusCell;
    if (focusCell == null || !this.selectionMgr.isFocusCell(rowId, colId)) {
      // Make target cell the focus cell
      this.selectionMgr.selectCell(rowId, colId, false, true);
      focusCell = this.focusCell;
    }
    let em = editingMachine;
    em._currentEditCell = focusCell; // Set this now in case refresh() does synchronous rendering

    // Kick the Grid to rerender
    this.refresh();
    editingMachine.onBeginEdit(rowId, colId, column, focusCell);
    return true;
  }

  /**
   * End an edit session.
   * Note that this is equivalent to calling editMachine.endEdit().
   * @param {boolean} cancel true to cancel the edit, false to commit.
   */
  endEdit(cancel) {
    let editingMachine = this.props.editingMachine;
    if (editingMachine == null) {
      return;
    }
    if (editingMachine.currentEditCell == null) {
      // Nothing to do
      return;
    }
    this.doEndEdit(cancel, editingMachine, editingMachine.currentEditCell, async (rowId, colId, column) => {
      if (editingMachine.useOnAfterEditAsync) {
        await editingMachine.onAfterEditAsync(rowId, colId, column);
      } else {
        editingMachine.onAfterEdit(rowId, colId, column);
      }
    });
  }
  async doEndEdit(cancel, editingMachine, currentEditCell, onAfterEdit) {
    let column = this.getColumnFromId(currentEditCell.colId);
    if (column) {
      if (cancel) {
        editingMachine.onCancelEdit(currentEditCell.rowId, currentEditCell.colId, column);
      } else {
        await onAfterEdit(currentEditCell.rowId, currentEditCell.colId, column);
      }
      if (this._editCellDiv && this._editCellDiv.ownerDocument && column.commitOnFocusLoss) {
        this._editCellDiv.ownerDocument.removeEventListener("focusin", this.onEditCellFocusHandler, {
          capture: true
        });
        this._editCellDiv = null;
        editingMachine.clearCurrentEditorInfo();
      }
    }

    // Kick the Grid to rerender
    if (cancel || !this.isVariableRowHeight) {
      // Just need to rerender
      this.refresh();
    } else {
      // We have ended the edit and the data value may have changed
      // With variable row height the row height may change as a result of the data change
      // so we need a heavier hammer here
      // I am not convinced that this is really working and will properly resize the cell if needed ...
      if (column) {
        this.recomputeGridSize([currentEditCell]);
      }
    }
    this.setFocusInGrid();
  }
  get isEditing() {
    let editMachine = this.props.editingMachine;
    if (editMachine == null) {
      return false;
    } else {
      return editMachine.isEditing;
    }
  }
  onFocusCellChangeInternal() {
    this.endEdit(false);
    if (this.props.onFocusCellChange) {
      this.props.onFocusCellChange(this.selectionMgr.selectionMachine);
    }
  }
  onEditCellFocusHandler(evt) {
    Assert.assert(evt !== undefined, "FocusOut event handler didn't get an event");
    if (!ClassUtils.isInstanceOf(evt, FocusEvent)) {
      Assert.unexpected("FocusOut event handler got event that wasn't a FocusEvent");
    }
    if (!this._editCellDiv) {
      return;
    }
    const event = evt;
    let isFocusLoss = false;
    const goingToTarget = event.target;
    if (goingToTarget === null || !ClassUtils.isInstanceOf(goingToTarget, HTMLElement)) {
      // It's not an Element. Don't know what that means, but it isn't good, so this is a focus loss
      isFocusLoss = true;
    } else {
      // First, get the layer info for the target and the edit cell renderer
      const targetLayerInfo = getLayerAndRootForElement(goingToTarget);

      // CAT-1750: If we are on a "overlay" layer, we never lose focus
      if (targetLayerInfo?.isOverlay) {
        return;
      }
      const editCellLayerInfo = getLayerAndRootForElement(this._editCellDiv);

      // If either one of them is the body, it's s a focus loss
      if (targetLayerInfo === null || editCellLayerInfo === null) {
        if (targetLayerInfo === null && editCellLayerInfo === null) {
          // If they're both on the body and the target isn't within the edit cell, it's a focus loss
          isFocusLoss = !ElementUtils.isAncestorOf(goingToTarget, this._editCellDiv);
        } else if (targetLayerInfo === null) {
          // If the target is on the body, then it's a focus loss because they're clicking out of the edit cell.
          isFocusLoss = true;
        } else if (editCellLayerInfo === null) {
          // If the target not on the body and the edit cell is, then the target is on the popup, and it's not a focus loss.
          // I know we already initialized it to false, but for code readability, we're setting it again.
          isFocusLoss = false;
        }
      } else {
        // Now get the z-indexes of the layer roots
        let targetZIndex = ElementUtils.getCssAsNumber("z-index", 0, targetLayerInfo.root);
        let editCellZIndex = ElementUtils.getCssAsNumber("z-index", 0, editCellLayerInfo.root);
        if (targetZIndex === editCellZIndex) {
          // If the z-indexes are the same, they're children of the same layer root.
          // So, we figure out which is rendered first in the layer root, putting it lower in the stack.
          // Their indexes in the stack are effectively z-indexes.
          const children = targetLayerInfo.root.children;
          let child;
          for (let i = 0; i < children.length; i++) {
            child = children.item(i);
            if (ElementUtils.compareElements(child, targetLayerInfo.layer)) {
              targetZIndex = i;
            }
            if (ElementUtils.compareElements(child, editCellLayerInfo.layer)) {
              editCellZIndex = i;
            }
          }
        }
        if (targetZIndex === editCellZIndex) {
          // If they're on the same z-index and the target isn't within the edit cell, it's a focus loss
          if (!ElementUtils.isAncestorOf(goingToTarget, this._editCellDiv)) {
            isFocusLoss = true;
          }
        } else if (targetZIndex > editCellZIndex) {
          // If the target is above the edit cell, it's a popup from the edit cell, so it's not a focus loss.
          // I know we already initialized it to false, but for code readability, we're setting it again.
          isFocusLoss = false;
        } else {
          // If the target is below the edit cell, it was already there when the edit cell was created, so it's a focus loss
          isFocusLoss = true;
        }
      }

      //Sometimes, the editor shouldn't close even when there is focus loss. This allows an override option for those
      //special cases. We call the user-defined function here to give it a chance to keep the editor open.
      if (isFocusLoss && this.props.keepEditorOpenOnFocusLoss !== undefined) {
        isFocusLoss = !this.props.keepEditorOpenOnFocusLoss(goingToTarget);
      }
    }

    // If it's a focus loss, end the edit
    if (isFocusLoss) {
      this.endEdit(false);
    }
  }
  isEditCell(rowIndex, columnIndex) {
    if (!this.isEditing) {
      return false;
    }
    let editingMachine = this.props.editingMachine;
    let editCellColIndex = this.getColumnIndexFromId(editingMachine.currentEditCell.colId);
    if (editCellColIndex !== columnIndex) {
      return false;
    }
    let editCellRowIndex = this.getRowIndexFromId(editingMachine.currentEditCell.rowId);
    return editCellRowIndex === rowIndex;
  }
  get columnVisibilityMachine() {
    return this._columnVisibilityMachine;
  }

  /**
   * Hides the indicated leaf.
   * @param {DataGridColumn} column
   */
  hideColumn(column) {
    if (!column.canHideColumn || !column.visible || this.isColumnFrozen(column)) {
      return;
    }
    column.visible = false;
    this.onColumnVisibilityChanged();
  }

  /**
   * Shows the indicated column.
   * @param {DataGridColumn} column
   */
  showColumn(column) {
    if (column.visible) {
      return;
    }
    column.visible = true;
    this.onColumnVisibilityChanged();
  }

  /**
   * Shows dialog to allow user to show/hide columns.
   */
  showColumnVisibilityDlg() {
    PopupManager.show(/*#__PURE__*/React.createElement(ColumnVisibilityDlg, {
      columnVisibilityMachine: this.columnVisibilityMachine,
      columns: this.currentColumns
    }));
  }
  onColumnVisibilityChanged() {
    this.initializeCachedColumnInfo(this.currentColumns, this.props.showColumnHeaders);
    if (this.cellMeasurerCache) {
      this.cellMeasurerCache.clearAll();
    }
    this.recomputeGridSize();
    this.refresh(true);
    this.forceUpdate();
  }
  get currentColumns() {
    return this._currentColumns;
  }
  setCurrentColumns(columns) {
    if (columns === this._currentColumns) {
      return;
    }
    this._currentColumns = columns;
    this.onColumnVisibilityChanged();
    this.props.onCurrentColumnsChange?.(columns);
  }

  /**
   * Moves columns within a group or at the top-level
   * @param columnsToMove Array of columns to move
   * @param insertBeforeColumn Move the columns to just before this column (assumes columnsToMove and insertBeforeColumn have the same parent column).
   * If null, the columns are moved to the end.   */
  moveColumns(columnsToMove, insertBeforeColumn) {
    if (!this.props.allowColumnDragToReorder) {
      DataGrid.LOGGER.info(`moveColumns Method is not allowed since props.allowColumnDragToReorder = false`);
      return;
    }
    if (columnsToMove.length === 0) {
      DataGrid.LOGGER.info(`moveColumns columnsToMove.length = 0. Nothing to move.`);
      return;
    }
    if (this.props.onBeforeMoveColumns) {
      this.props.onBeforeMoveColumns(this.currentColumns, columnsToMove, insertBeforeColumn);

      // Note that if this callback is implemented, it is up to the handler to mutate the grid columns and rerender
      return;
    }
    let topLevelColumns = DataGridUtils.moveColumns(this.currentColumns, columnsToMove, insertBeforeColumn);
    if (topLevelColumns == null) {
      // Nothing done
      return;
    }

    // This changes the currentColumns Array which is the heavy hammer to invalidate everything
    this.setCurrentColumns(topLevelColumns);
  }
  get leafColumns() {
    return this._leafColumns;
  }
  get visibleLeafColumns() {
    return this._visibleLeafColumns;
  }
  rowShift(index) {
    if (this.props.rowShift) {
      return this.props.rowShift(this.getRowIdFromIndex(index));
    }
    return 0;
  }
  cellRangeRenderer(params) {
    this.beforeSectionRendered(params);

    // Patch the cellSizeAndPositionManager so the this.props.rowShift callback
    // will bump the offset.  TODO: only works on the last row.

    let rowSizeAndPositionManager = params.rowSizeAndPositionManager;
    if (!rowSizeAndPositionManager.hasOwnProperty("monkeyPatch"))
      // if it hasn't been patched before
      {
        if (rowSizeAndPositionManager._cellSizeAndPositionManager == null) {
          throw new Error("DataGrid: Cannot patch cellSizeAndPositionManager");
        }
        // get old definition of "getSizeAndPositionOfCell"
        let oldFunc = rowSizeAndPositionManager._cellSizeAndPositionManager.getSizeAndPositionOfCell.bind(rowSizeAndPositionManager._cellSizeAndPositionManager);
        // make a new definition (which calls the old definition)
        let newFunc = index => {
          let oldVal = oldFunc(index);
          const shiftAmount = this.rowShift(index);
          if (shiftAmount !== 0 && oldVal.shift !== shiftAmount) {
            // we should shift, and we haven't shifted that amount before
            oldVal.offset += shiftAmount - (oldVal.shift ?? 0);
            oldVal.shift = shiftAmount;
          }
          return oldVal;
        };
        // replace the old definition with the new one
        rowSizeAndPositionManager._cellSizeAndPositionManager.getSizeAndPositionOfCell = newFunc;
        // record that we've patched this world
        rowSizeAndPositionManager.monkeyPatch = true;
      }
    let onRenderComplete = this.props.onBodyGridRendered && params.parent === this.bodyGrid ? this.onBodyGridCellRangeRendered : undefined;
    return [/*#__PURE__*/React.createElement(DataGridCellRange, {
      key: 1,
      dataFreshness: this.props.dataFreshness,
      refreshCount: this._refreshCount,
      params: params,
      dataGrid: this,
      multiGrid: this.multiGrid,
      groupedHeaderCellRangeRenderer: this.groupedHeaderCellRangeRenderer,
      visibleLeafColumns: this._visibleLeafColumns,
      onRenderComplete: onRenderComplete
    })];
  }
  onBodyGridCellRangeRendered() {
    if (this.props.onBodyGridRendered) {
      let rect = this.containerDiv?.querySelector(".ab-is-content-grid :first-child")?.getBoundingClientRect();
      if (rect) {
        // DataGrid.LOGGER.info(`onBodyGridCellRangeRendered (${rect.width} X ${rect.height})`);

        this.props.onBodyGridRendered(rect.width, rect.height);
      } else {
        // DataGrid.LOGGER.info(`onBodyGridCellRangeRendered no rect`);
      }
    }
  }
  groupedHeaderCellRangeRenderer(params) {
    let {
      cellCache,
      cellRenderer,
      columnSizeAndPositionManager,
      columnStartIndex,
      columnStopIndex,
      deferredMeasurementCache,
      horizontalOffsetAdjustment,
      isScrolling,
      parent,
      // Grid
      rowSizeAndPositionManager,
      // rowStartIndex,
      // rowStopIndex,
      styleCache,
      verticalOffsetAdjustment,
      visibleColumnIndices,
      visibleRowIndices
    } = params;
    let cellsToBeRendered = [];

    // Browsers have native size limits for elements (eg Chrome 33M pixels, IE 1.5M pixes).
    // User cannot scroll beyond these size limitations.
    // In order to work around this, ScalingCellSizeAndPositionManager compresses offsets.
    // We should never cache styles for compressed offsets though as this can lead to bugs.
    // See issue #576 for more.
    const areOffsetsAdjusted = columnSizeAndPositionManager.areOffsetsAdjusted() || rowSizeAndPositionManager.areOffsetsAdjusted();
    const canCacheStyle = !isScrolling && !areOffsetsAdjusted;

    //let renderCache: { [s: string]: boolean } = {};
    let renderCache = new Set();

    // Note that [columnStartIndex, columnStopIndex] are zero-based indices in the topRightGrid,
    // so we need to be careful since our _visibleLeafColumns may contain frozen columns which are rendered in the topLeftGrid
    for (let visColumnIndex = columnStartIndex; visColumnIndex <= columnStopIndex; visColumnIndex++) {
      let col = this._visibleLeafColumns[visColumnIndex + this.frozenColumnCount];
      while (col != null) {
        if (col.cellSlot == null) {
          col = col.parentColumn;
          continue;
        }

        // Per above note, be careful with this columnIndex which is not the same as visColumnIndex
        let columnIndex = col.cellSlot.columnIndex;
        let rowIndex = col.cellSlot.rowIndex;
        let key = `${rowIndex}-${columnIndex}`;
        if (renderCache.has(key)) {
          break;
        }
        let rowDatum = rowSizeAndPositionManager.getSizeAndPositionOfCell(rowIndex);
        let columnDatum = columnSizeAndPositionManager.getSizeAndPositionOfCell(columnIndex - this.frozenColumnCount);
        let isVisible = columnIndex >= visibleColumnIndices.start + this.frozenColumnCount && columnIndex <= visibleColumnIndices.stop + this.frozenColumnCount && rowIndex >= visibleRowIndices.start && rowIndex <= visibleRowIndices.stop;
        let style;

        // Cache style objects so shallow-compare doesn't re-render unnecessarily.
        if (canCacheStyle && styleCache[key]) {
          style = styleCache[key];
        } else {
          // In deferred mode, cells will be initially rendered before we know their size.
          // Don't interfere with CellMeasurer's measurements by setting an invalid size.
          if (deferredMeasurementCache && !deferredMeasurementCache.has(rowIndex, columnIndex - this.frozenColumnCount)) {
            // Position not-yet-measured cells at top/left 0,0,
            // And give them width/height of 'auto' so they can grow larger than the parent Grid if necessary.
            // Positioning them further to the right/bottom influences their measured size.
            style = {
              height: "auto",
              left: 0,
              position: "absolute",
              top: 0,
              width: "auto"
            };
          } else {
            let height = rowDatum.size;
            for (let i = 1; i < col.cellSlot.rowSpan; i++) {
              height += rowSizeAndPositionManager.getSizeAndPositionOfCell(rowIndex + i).size;
            }
            style = {
              height: height,
              left: columnDatum.offset + horizontalOffsetAdjustment,
              position: "absolute",
              top: rowDatum.offset + verticalOffsetAdjustment,
              width: this.getColumnCurrentWidth(col)
            };
            styleCache[key] = style;
          }
        }
        let cellRendererParams = {
          columnIndex: columnIndex - this.frozenColumnCount,
          isScrolling,
          isVisible,
          key,
          parent,
          rowIndex,
          style
        };
        let renderedCell;

        // Avoid re-creating cells while scrolling.
        // This can lead to the same cell being created many times and can cause performance issues for "heavy" cells.
        // If a scroll is in progress- cache and reuse cells.
        // This cache will be thrown away once scrolling completes.
        // However if we are scaling scroll positions and sizes, we should also avoid caching.
        // This is because the offset changes slightly as scroll position changes and caching leads to stale values.
        // For more info refer to issue #395
        if (isScrolling && !horizontalOffsetAdjustment && !verticalOffsetAdjustment) {
          if (!cellCache[key]) {
            cellCache[key] = cellRenderer(cellRendererParams);
          }
          renderedCell = cellCache[key];

          // If the user is no longer scrolling, don't cache cells.
          // This makes dynamic cell content difficult for users and would also lead to a heavier memory footprint.
        } else {
          renderedCell = cellRenderer(cellRendererParams);
        }
        if (renderedCell == null || renderedCell === false) {
          continue;
        }
        cellsToBeRendered.push({
          rowIndex,
          columnIndex,
          cell: renderedCell
        });
        renderCache.add(key);
        col = col.parentColumn;
      }
    }
    // Sort Cells so that they end up ordered in rows and columns
    cellsToBeRendered.sort((a, b) => {
      if (a.rowIndex < b.rowIndex) {
        return -1;
      }
      if (a.rowIndex > b.rowIndex) {
        return 1;
      }
      if (a.columnIndex < b.columnIndex) {
        return -1;
      }
      if (a.columnIndex > b.columnIndex) {
        return 1;
      }
      return 0;
    });

    // convert the array of objects with index info to just an array
    // of the ReactNodes
    return cellsToBeRendered.map(x => x.cell);
  }
  getColumnHeaderSelectionClass(colId) {
    if (this.selectionMgr.selectionRegion.isSelectAll) {
      return " ab-selected-cells";
    }
    if (this.selectionMgr.isColumnSelected(colId)) {
      return " ab-selected";
    }
    if (this.selectionMgr.selectionRegion.hasRowsSelected || this.selectionMgr.selectionRegion.columnHasSelectedCells(colId)) {
      return " ab-selected-cells";
    }
    return null;
  }
  getRowHeaderSelectionClass(rowId) {
    if (this.selectionMgr.selectionRegion.isSelectAll) {
      return " ab-selected-cells";
    }
    if (this.selectionMgr.isRowSelected(rowId)) {
      return " ab-selected";
    }
    if (this.selectionMgr.selectionRegion.hasColumnsSelected || this.selectionMgr.selectionRegion.rowHasSelectedCells(rowId)) {
      return " ab-selected-cells";
    }
    return null;
  }

  /**
   * Returns the viewport width as used by the column width calculations.
   * @returns {number}
   */
  getBodyGridViewPortWidth() {
    let width = this.innerGridWidth - this._verticalScrollBarSize;
    if (this.props.showRowHeaderColumn) {
      width -= this.props.rowHeaderWidth;
    }
    return width;
  }
  getBodyGridScrollWidth() {
    if (this.bodyGrid == null) {
      return 0;
    }
    return this.bodyGrid._scrollingContainer.scrollWidth;
  }

  /**
   * Returns the count of fixed rows in the column header portion of the DataGrid.
   * If no column headers are used this will be zero.
   * For a simple flat set of columns, this will be 1.
   * If grouped columns are used, this will be the maximum depth of the grouped columns.
   * @returns {number}
   */
  get fixedRowCount() {
    return this._fixedRowCount;
  }
  static renderColumnHeaderSortImage(dgCol, onSort, sortDirection, onClearSort) {
    let sortClick;
    if (dgCol.isLeafColumn && dgCol.allowSort && dgCol.label && sortDirection) {
      let tooltip;
      if (sortDirection === SortDirection.ASC) {
        // Always transition from ASC => DESC
        sortClick = e => onSort(dgCol.id, SortDirection.DESC);
        tooltip = AbReactResources.dataGrid_columnHeader_ascendingSortIconTooltip();
      } else {
        // We have DESC sort
        if (onClearSort) {
          // We have a onClearSort callback, so this implies our 3-state toggle
          sortClick = e => onClearSort(dgCol.id);
          tooltip = AbReactResources.dataGrid_columnHeader_descendingSortResetIconTooltip();
        } else {
          // Toggle back to ASC
          sortClick = e => onSort(dgCol.id, SortDirection.ASC);
          tooltip = AbReactResources.dataGrid_columnHeader_descendingSortIconTooltip();
        }
      }
      const ascending = sortDirection === SortDirection.ASC;
      const icon = ascending ? "absdk:sort-ascending-caret" : "absdk:sort-descending-caret";
      return /*#__PURE__*/React.createElement(AbImage, {
        className: "ab-sort-column-icon",
        onClick: sortClick,
        image: icon,
        tooltip: tooltip
      });
    } else {
      sortClick = e => {
        onSort(dgCol.id, SortDirection.ASC);
      };
      return /*#__PURE__*/React.createElement(AbImage, {
        className: "ab-sort-column-icon potential",
        onClick: sortClick,
        image: "absdk:sort-caret",
        tooltip: AbReactResources.dataGrid_columnHeader_sortableIconTooltip()
      });
    }
  }
  get frozenColumnCount() {
    const {
      frozenColumnCount
    } = this.props;
    if (frozenColumnCount === undefined) {
      return 0;
    }
    if (frozenColumnCount < 0 || frozenColumnCount >= this.currentColumns.length) {
      return 0;
    }
    return Math.floor(frozenColumnCount);
  }

  /**
   * Validates column properties that related to use of frozenColumnCount.
   * @private
   */
  validateFrozenColumns(frozenColumnCount, columns) {
    if (frozenColumnCount <= 0) {
      // Not using frozen columns
      this.removeWheelEventListenerOnBottomLeftGrid();
      return;
    }

    // Need to satisfy constraints outlined in frozenColumnCount prop documentation
    if (frozenColumnCount >= columns.length) {
      // Nothing to scroll (we must have at least 1 scrollable column
      DataGrid.LOGGER.warn(`frozenColumnCount (${frozenColumnCount}) must be less than columns.length (${columns.length}). Assuming value 0.`);
      this.removeWheelEventListenerOnBottomLeftGrid();
      return;
    }
    for (let n = 0; n < frozenColumnCount; n++) {
      let col = columns[n];
      if (!col.isLeafColumn) {
        this.removeWheelEventListenerOnBottomLeftGrid();
        throw new Error("Frozen columns must be leaf columns.");
      }
      col.visible = true;
      if (col.widthPercent !== undefined) {
        DataGrid.LOGGER.warn(`column id: ${col.id} cannot have widthPercent when frozenColumnCount is used. Setting to fixed width.`);
        col.width = col.actualWidth === undefined ? 125 : col.actualWidth;
        col.widthPercent = undefined;
      }
    }

    // Per MH-31580, set up our wheel event listener in the bottomLeftGrid
    this.addWheelEventListenerOnBottomLeftGrid();
  }
  isColumnFrozen(column) {
    for (let n = 0; n < this.frozenColumnCount; n++) {
      if (this.currentColumns[n] === column) {
        return true;
      }
    }
    return false;
  }
}

/**
 * Gets the layer an element is on and its root element
 * CAT-1750: There are some elements that are NOT in managed layers, but really are in "overlay" layers.
 * Those elements include the Kendo widgets, and the grid's own mouse-move-tracker.
 * This is fragile: we may have to add more class names in the future.
 * @param element
 */
_defineProperty(DataGrid, "LOGGER", LogUtils.getLogger(__filename));
_defineProperty(DataGrid, "defaultProps", {
  rowHeaderWidth: 40,
  altRowClassName: null,
  allowColumnResize: true,
  editable: false,
  doubleClickToEditCell: true,
  recomputeGridSizeOnDataFreshnessChange: true,
  columnResizeMode: ColumnResizeMode.PRESERVE_WIDTH_PERCENTS,
  useNativeTooltipOnCells: true,
  allowColumnDragToReorder: false,
  lockedColumnCount: 0
});
function getLayerAndRootForElement(element) {
  let child = element;
  while (child.parentElement !== null) {
    if (ReactUtils.hasClassName(child.parentElement, LayerGroup.LAYER_ROOT_CLASSNAME)) {
      return {
        layer: child,
        root: child.parentElement,
        isOverlay: false
      };
    } else if (ReactUtils.hasClassName(child, "k-widget") || ReactUtils.hasClassName(child, "mouse-move-tracker-overlay")) {
      return {
        layer: child,
        root: child,
        isOverlay: true
      };
    }
    child = child.parentElement;
  }
  return null;
}
/**
 * Render an empty cell for an overscroll row
 */
class OverscrollCell extends React.Component {
  render() {
    return /*#__PURE__*/React.createElement("div", {
      style: this.props.style,
      className: "ab-overscroll-grid-cell",
      key: this.props.key
    });
  }
}
//# sourceMappingURL=DataGrid.js.map
